import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import * as echarts from 'echarts';
import './Landing.css';

const Landing = () => {
  const { t, i18n } = useTranslation();
  const [riskProfile, setRiskProfile] = useState('Neutral');
  const [proofRiskProfile, setProofRiskProfile] = useState('Neutral');
  const [isScrolled, setIsScrolled] = useState(false);
  const [openModal, setOpenModal] = useState(null);
  const [faqCategory, setFaqCategory] = useState('all');
  const [faqSearch, setFaqSearch] = useState('');
  const [expandedFaq, setExpandedFaq] = useState(null);

  const performanceChartRef = useRef(null);
  const proofChartRef = useRef(null);
  const performanceChartInstanceRef = useRef(null);
  const proofChartInstanceRef = useRef(null);
  const riskToggleRef = useRef(null);
  const proofToggleRef = useRef(null);

  // Language toggle handler
  const handleLanguageToggle = (newLang) => {
    i18n.changeLanguage(newLang);
  };

  // Profile themes
  const profileThemes = {
    Averse: {
      accent: "#59dcb6",
      shadow: "rgba(89, 220, 182, 0.35)",
      gradientA: "rgba(89, 220, 182, 0.16)",
      gradientB: "rgba(89, 220, 182, 0.08)",
      orbitA: "rgba(89, 220, 182, 0.55)",
      orbitB: "rgba(89, 220, 182, 0.24)",
    },
    Neutral: {
      accent: "#49a9ff",
      shadow: "rgba(73, 169, 255, 0.35)",
      gradientA: "rgba(73, 169, 255, 0.16)",
      gradientB: "rgba(73, 169, 255, 0.08)",
      orbitA: "rgba(73, 169, 255, 0.52)",
      orbitB: "rgba(73, 169, 255, 0.24)",
    },
    Seeker: {
      accent: "#f9a53d",
      shadow: "rgba(249, 165, 61, 0.28)",
      gradientA: "rgba(249, 165, 61, 0.18)",
      gradientB: "rgba(249, 165, 61, 0.1)",
      orbitA: "rgba(249, 165, 61, 0.6)",
      orbitB: "rgba(249, 165, 61, 0.3)",
    },
  };

  // Performance data for hero chart
  const performanceData = {
    Averse: {
      portfolio: [100, 100.6, 101.2, 100.9, 101.8, 102.6, 103.4, 103.0, 104.2, 105.3, 106.4, 108.0],
      sp500: [100, 100.8, 101.6, 100.9, 102.4, 103.6, 104.8, 105.5, 106.2, 107.4, 108.6, 110.0]
    },
    Neutral: {
      portfolio: [100, 101.1, 102.4, 101.6, 103.8, 105.5, 106.9, 108.3, 109.8, 110.5, 111.2, 112.0],
      sp500: [100, 100.8, 101.6, 100.9, 102.4, 103.6, 104.8, 105.5, 106.2, 107.4, 108.6, 110.0]
    },
    Seeker: {
      portfolio: [100, 101.8, 103.6, 102.2, 104.9, 107.3, 109.8, 111.2, 112.6, 113.8, 114.6, 115.0],
      sp500: [100, 100.8, 101.6, 100.9, 102.4, 103.6, 104.8, 105.5, 106.2, 107.4, 108.6, 110.0]
    }
  };

  // Allocation data
  const allocationData = {
    Averse: { core: 60, leveraged: 0, riskfree: 40 },
    Neutral: { core: 80, leveraged: 0, riskfree: 20 },
    Seeker: { core: 70, leveraged: 30, riskfree: 0 }
  };

  // Proof section data (embedded from CSV)
  const proofData = {
    years: ['2006', '2007', '2008', '2009', '2010', '2011', '2012', '2013', '2014', '2015', '2016', '2017', '2018', '2019', '2020', '2021', '2022', '2023', '2024', '2025'],
    spy: [15, 5, -46, 23, 14, 2, 15, 28, 13, 1, 11, 20, -5, 27, 17, 25, -20, 23, 22, 14],
    Neutral: {
      data: [28.3, 7.3, -33.5, 24.0, 18.1, 6.0, 21.9, 28.3, 18.8, -0.7, 15.0, 32.4, -1.8, 37.4, 26.9, 26.1, -22.3, 34.6, 21.5, 19.8],
      metrics: {
        beatsBenchmark: 85,
        avgReturn: 15,
        volatility: 18,
        maxDrawdown: -33.5,
        excessReturn: 5
      }
    },
    Seeker: {
      data: [37.7, 9.8, -45.5, 31.3, 24.2, 8.0, 29.2, 37.9, 25.2, -1.0, 19.9, 42.0, -2.1, 49.9, 31.7, 34.8, -29.7, 46.1, 28.8, 24.9],
      metrics: {
        beatsBenchmark: 90,
        avgReturn: 20,
        volatility: 24,
        maxDrawdown: -45.5,
        excessReturn: 10
      }
    },
    Averse: {
      data: [14.1, 4.1, -17.5, 15.0, 10.0, 5.2, 11.2, 14.2, 9.4, -0.5, 7.6, 16.2, -0.9, 19.0, 23.9, 13.2, -9.0, 17.3, 10.8, 12.5],
      metrics: {
        beatsBenchmark: 25,
        avgReturn: 9,
        volatility: 10,
        maxDrawdown: -17.5,
        excessReturn: -2
      }
    }
  };

  // FAQ data - full list from HTML templates
  const faqData = i18n.language === 'es' ? [
    { id: 1, question: "¿Qué es Altradenative?", answer: "Altradenative es una plataforma de análisis basada en suscripción que proporciona perspectivas de inversión generadas algorítmicamente para los mercados de acciones globales. Ayuda a las personas a optimizar sus estrategias de inversión utilizando dashboards basados en datos, sin ofrecer asesoramiento de inversión ni ejecutar operaciones.", category: "getting-started" },
    { id: 2, question: "¿Cómo funciona Altradenative?", answer: "En su núcleo, Altradenative utiliza un algoritmo propietario que monitorea la acción de precios y las tendencias de volatilidad en los principales índices globales. El algoritmo se adapta dinámicamente a diferentes escenarios de riesgo y proporciona métricas de rendimiento periódicas y a largo plazo, así como perspectivas backtesteadas a través de dashboards.", category: "platform" },
    { id: 3, question: "¿Es Altradenative un bróker o asesor de inversiones?", answer: "No. Altradenative no es un bróker, no ejecuta operaciones y no proporciona asesoramiento de inversión personalizado. Todas las perspectivas son únicamente con fines informativos.", category: "legal" },
    { id: 4, question: "¿Qué obtengo con una suscripción?", answer: "Los suscriptores obtienen acceso a dashboards analíticos que ofrecen perspectivas dinámicas del mercado, métricas de rendimiento y datos históricos de estrategia. Podés elegir entre planes diarios o mensuales, y hay una prueba gratuita disponible. <a href='/subscriptions'>Ver planes de suscripción</a>.", category: "subscription" },
    { id: 5, question: "¿Cómo uso las perspectivas proporcionadas?", answer: "Usás los dashboards para informar tus propias decisiones de inversión. Si deseás actuar sobre las perspectivas, debés realizar operaciones de forma independiente a través de tu bróker elegido.", category: "platform" },
    { id: 6, question: "¿Qué activos e índices cubre Altradenative?", answer: "Altradenative ofrece dashboards enfocados en índices de acciones globales (por ejemplo, S&P 500, Nasdaq 100), ETFs apalancados y activos libres de riesgo como efectivo o instrumentos de deuda del gobierno de Estados Unidos a corto plazo.", category: "investment" },
    { id: 7, question: "¿Hay una prueba gratuita?", answer: "Sí. Podés acceder al Dashboard de Mercados Globales durante cinco sesiones de trading sin necesidad de tarjeta de crédito. <a href='/subscriptions'>Comenzá tu prueba gratuita</a>.", category: "getting-started" },
    { id: 8, question: "¿Cómo están protegidos mis datos?", answer: "Altradenative utiliza medidas de seguridad estándar de la industria y cumple con las leyes de protección de datos aplicables. Por favor, consultá nuestra <a href='#' onClick={(e) => { e.preventDefault(); handleModalClose(); handleModalOpen('privacy'); }} style={{color: 'var(--accent)', cursor: 'pointer'}}>Política de Privacidad</a> para más detalles.", category: "security" },
    { id: 9, question: "¿Puede Altradenative garantizar resultados de inversión?", answer: "No. El rendimiento pasado, incluidos los datos backtesteados, no garantiza resultados futuros. Invertir en acciones y ETFs conlleva el riesgo de pérdida de capital.", category: "legal" },
    { id: 10, question: "¿Quién es dueño de Altradenative?", answer: "Altradenative es una marca registrada en Estados Unidos, propiedad de Hedgit LLC y operada en asociación con DyHedge y Valcereal.", category: "legal" },
    { id: 11, question: "¿Cómo cancelo mi suscripción?", answer: "Podés cancelar tu suscripción en cualquier momento a través de la configuración de tu cuenta. Las suscripciones mensuales se renuevan automáticamente a menos que se cancelen.", category: "subscription" },
    { id: 12, question: "¿Quién puede usar Altradenative?", answer: "Debés tener al menos 18 años para usar la plataforma.", category: "legal" },
    { id: 13, question: "¿Cómo contacto al soporte?", answer: "Para preguntas o inquietudes, por favor contactanos en <a href='mailto:contact@hedgit.ai'>contact@hedgit.ai</a>.", category: "contact" },
    { id: 14, question: "Cómo registrarse y comenzar tu prueba gratuita", answer: "Visitá nuestra <a href='/subscriptions'>página de suscripciones</a> y hacé clic en el botón 'Comenzar Prueba Gratuita'. No se requiere tarjeta de crédito. Obtendrás 5 sesiones de trading con acceso completo al Dashboard de Mercados Globales.", category: "getting-started" },
    { id: 15, question: "Navegando el dashboard", answer: "El dashboard proporciona tres secciones principales: Asignación de portafolio, Gráficos de rendimiento y Configuración de perfil de riesgo. Usá el toggle de perfil de riesgo para ver cómo los diferentes niveles de riesgo afectan la asignación de tu portafolio y las métricas de rendimiento.", category: "getting-started" },
    { id: 16, question: "Entendiendo los perfiles de riesgo (Cauto, Balanceado, Agresivo)", answer: "Cauto: Menor riesgo, asignación más conservadora con mayor exposición a efectivo/activos libres de riesgo. Balanceado: Enfoque equilibrado con riesgo moderado. Agresivo: Mayor tolerancia al riesgo con exposición a ETF apalancado para retornos potencialmente más altos.", category: "getting-started" },
    { id: 17, question: "Opciones de suscripción y precios", answer: "Ofrecemos planes Diario ($10 compra única), Mensual Mercados Globales ($29/mes) y Mensual Todos los Dashboards ($49/mes). Todos los planes incluyen acceso completo al dashboard. <a href='/subscriptions'>Ver todos los planes</a>.", category: "subscription" },
    { id: 18, question: "Cómo actualizar, reducir o cancelar", answer: "Podés gestionar tu suscripción a través de la configuración de tu cuenta. Las actualizaciones tienen efecto inmediato, mientras que las reducciones se aplican en el próximo ciclo de facturación. Las cancelaciones se pueden realizar en cualquier momento.", category: "subscription" },
    { id: 19, question: "Métodos de pago y seguridad", answer: "Aceptamos las principales tarjetas de crédito y procesamos pagos de forma segura a través de proveedores de terceros. Toda la información de pago está encriptada y nunca se almacena en nuestros servidores.", category: "subscription" },
    { id: 20, question: "Resumen del dashboard y análisis", answer: "El dashboard muestra asignaciones de portafolio en tiempo real, gráficos de rendimiento que comparan la estrategia de Altradenative con el S&P 500, y datos históricos backtesteados que abarcan 20 años.", category: "platform" },
    { id: 21, question: "Métricas de rendimiento explicadas", answer: "Las métricas clave incluyen: % de Años que Superó al SPY, Retorno Promedio Anual, Volatilidad, Relación Riesgo-Retorno y Máxima Pérdida Anual. Estas te ayudan a entender el rendimiento histórico de la estrategia.", category: "platform" },
    { id: 22, question: "Backtesting y datos históricos", answer: "Nuestro algoritmo ha sido backtesteado con más de 20 años de datos históricos. Esto incluye el rendimiento durante diversas condiciones de mercado, incluyendo mercados alcistas, mercados bajistas y períodos de alta volatilidad.", category: "platform" },
    { id: 23, question: "Medidas de protección de datos", answer: "Utilizamos encriptación estándar de la industria, servidores seguros y auditorías de seguridad regulares. Tu información personal está protegida de acuerdo con GDPR, CCPA y otras regulaciones de protección de datos aplicables.", category: "security" },
    { id: 24, question: "Transferencias internacionales de datos", answer: "Como plataforma global, tus datos pueden ser procesados en países fuera del tuyo. Aseguramos que se implementen salvaguardas apropiadas y cumplimos con las leyes de protección de datos aplicables.", category: "security" },
    { id: 25, question: "Tus derechos respecto a los datos personales", answer: "Tenés derecho a acceder, corregir, eliminar o exportar tus datos personales. También podés oponerte a ciertas actividades de procesamiento. Contactanos en <a href='mailto:contact@hedgit.ai'>contact@hedgit.ai</a> para ejercer estos derechos.", category: "security" },
    { id: 26, question: "Cómo funciona el algoritmo", answer: "El algoritmo monitorea continuamente los principales índices globales, siguiendo la acción de precios y la volatilidad. Ajusta dinámicamente las asignaciones del portafolio entre ETFs principales, ETFs apalancados y activos libres de riesgo según las condiciones del mercado.", category: "investment" },
    { id: 27, question: "Qué índices y activos están cubiertos", answer: "Cubrimos S&P 500 (SPY/VOO), Nasdaq 100 (QQQ), versiones apalancadas (UPRO, TQQQ) y activos libres de riesgo (efectivo, letras del Tesoro). Hay tres opciones de dashboard disponibles: Mercados Globales, Tecnología Global e Híbrido.", category: "investment" },
    { id: 28, question: "Filosofía de gestión de riesgo", answer: "Nuestra filosofía sigue la sabiduría clásica de inversión: ser oportunista cuando otros tienen miedo y cauteloso cuando son codiciosos. El algoritmo aumenta la exposición al mercado durante oportunidades identificadas y la reduce cuando se detecta riesgo elevado.", category: "investment" },
    { id: 29, question: "Resumen de Términos de Servicio", answer: "Al usar Altradenative, aceptás nuestros Términos de Servicio. Puntos clave: proporcionamos solo análisis (no asesoramiento de inversión), debés tener 18+ años, las suscripciones se pueden cancelar en cualquier momento y toda inversión conlleva riesgo. <a href='#' class='faq-modal-link' data-modal='terms'>Leer Términos completos</a>.", category: "legal" },
    { id: 30, question: "Aspectos destacados de la Política de Privacidad", answer: "Recopilamos información personal mínima, la usamos solo para proporcionar servicios, no vendemos tus datos y cumplimos con las principales leyes de protección de datos. <a href='#' class='faq-modal-link' data-modal='privacy'>Leer Política de Privacidad completa</a>.", category: "legal" },
    { id: 31, question: "Avisos legales y limitaciones", answer: "Altradenative no garantiza resultados de inversión. El rendimiento pasado no garantiza resultados futuros. No somos un bróker ni asesor. Sos el único responsable de tus decisiones de inversión. Toda inversión conlleva riesgo de pérdida de capital.", category: "legal" },
    { id: 32, question: "Problemas de acceso", answer: "Si tenés problemas para acceder a tu dashboard, intentá limpiar la caché de tu navegador, verificar tu conexión a internet o cerrar sesión y volver a iniciar sesión. Si los problemas persisten, contactá a <a href='mailto:contact@hedgit.ai'>contact@hedgit.ai</a>.", category: "troubleshooting" },
    { id: 33, question: "Dashboard no se actualiza", answer: "Los datos del dashboard se actualizan diariamente. Si notás datos obsoletos, actualizá la página. Para problemas persistentes, verificá el estado de tu suscripción o contactá al soporte.", category: "troubleshooting" },
    { id: 34, question: "Problemas de suscripción", answer: "Si estás experimentando problemas de facturación, problemas de acceso después del pago o dificultades para gestionar tu suscripción, por favor contactá a <a href='mailto:contact@hedgit.ai'>contact@hedgit.ai</a> con los detalles de tu cuenta.", category: "troubleshooting" },
    { id: 35, question: "Cómo contactar al servicio al cliente", answer: "Enviános un correo a <a href='mailto:contact@hedgit.ai'>contact@hedgit.ai</a>. Normalmente respondemos dentro de 24-48 horas durante días hábiles.", category: "contact" },
    { id: 36, question: "Reportar problemas técnicos", answer: "Si encontrás errores, bugs o problemas técnicos, por favor enviá un correo a <a href='mailto:contact@hedgit.ai'>contact@hedgit.ai</a> con detalles sobre el problema, tu navegador y cualquier mensaje de error que veas.", category: "contact" },
    { id: 37, question: "Comentarios y sugerencias", answer: "¡Apreciamos tus comentarios! Enviá sugerencias, solicitudes de funciones o comentarios generales a <a href='mailto:contact@hedgit.ai'>contact@hedgit.ai</a>. Tu aporte nos ayuda a mejorar la plataforma.", category: "contact" }
  ] : [
    { id: 1, question: "What is Altradenative?", answer: "Altradenative is a subscription-based analytics platform that provides algorithmically generated investment insights for global stock markets. It helps individuals optimize their investment strategies using data-driven dashboards, without offering trading advice or executing trades.", category: "getting-started" },
    { id: 2, question: "How does Altradenative work?", answer: "At its core, Altradenative uses a proprietary algorithm that monitors price action and volatility trends across major global indices. The algorithm dynamically adapts to different risk scenarios and provides periodic, long-term performance metrics and back-tested insights via dashboards.", category: "platform" },
    { id: 3, question: "Is Altradenative a broker or investment advisor?", answer: "No. Altradenative is not a broker, does not execute trades, and does not provide personalized investment advice. All insights are for informational purposes only.", category: "legal" },
    { id: 4, question: "What do I get with a subscription?", answer: "Subscribers gain access to analytical dashboards that deliver dynamic market insights, performance metrics, and historical strategy data. You can choose between daily or monthly plans, and a free trial is available. <a href='/subscriptions'>View subscription plans</a>.", category: "subscription" },
    { id: 5, question: "How do I use the insights provided?", answer: "You use the dashboards to inform your own investment decisions. If you wish to act on the insights, you must place trades independently through your chosen broker.", category: "platform" },
    { id: 6, question: "What assets and indices does Altradenative cover?", answer: "Altradenative offers dashboards focused on global stock indices (e.g., S&P 500, Nasdaq 100), leveraged ETFs, and risk-free assets like cash or short-term U.S. government debt instruments.", category: "investment" },
    { id: 7, question: "Is there a free trial?", answer: "Yes. You can access the Global Markets Dashboard for five trading sessions without needing a credit card. <a href='/subscriptions'>Start your free trial</a>.", category: "getting-started" },
    { id: 8, question: "How is my data protected?", answer: "Altradenative uses industry-standard security measures and complies with applicable data protection laws. Please refer to our <a href='#' class='faq-modal-link' data-modal='privacy'>Privacy Policy</a> for details.", category: "security" },
    { id: 9, question: "Can Altradenative guarantee investment results?", answer: "No. Past performance, including backtested data, does not guarantee future results. Investing in stocks and ETFs carries the risk of capital loss.", category: "legal" },
    { id: 10, question: "Who owns Altradenative?", answer: "Altradenative is a United States-registered trademark owned by Hedgit LLC and operated in partnership with DyHedge and Valcereal.", category: "legal" },
    { id: 11, question: "How do I cancel my subscription?", answer: "You can cancel your subscription at any time via your account settings. Monthly subscriptions renew automatically unless canceled.", category: "subscription" },
    { id: 12, question: "Who can use Altradenative?", answer: "You must be at least 18 years old to use the platform.", category: "legal" },
    { id: 13, question: "How do I contact support?", answer: "For questions or concerns, please contact us at <a href='mailto:contact@hedgit.ai'>contact@hedgit.ai</a>.", category: "contact" },
    { id: 14, question: "How to sign up and start your free trial?", answer: "Visit our <a href='/subscriptions'>subscriptions page</a> and click on the 'Start Free Trial' button. No credit card is required. You'll get 5 trading sessions with full access to the Global Markets Dashboard.", category: "getting-started" },
    { id: 15, question: "Navigating the dashboard", answer: "The dashboard provides three main sections: Portfolio allocation, Performance charts, and Risk profile settings. Use the risk profile toggle to see how different risk levels affect your portfolio allocation and performance metrics.", category: "getting-started" },
    { id: 16, question: "Understanding risk profiles (Averse, Neutral, Seeker)", answer: "Averse: Lower risk, more conservative allocation with higher cash/risk-free exposure. Neutral: Balanced approach with moderate risk. Seeker: Higher risk tolerance with leveraged ETF exposure for potentially higher returns.", category: "getting-started" },
    { id: 17, question: "Subscription options and pricing", answer: "We offer Daily ($10 one-time), Monthly Global Markets ($29/month), and Monthly All Dashboards ($49/month) plans. All plans include full dashboard access. <a href='/subscriptions'>See all plans</a>.", category: "subscription" },
    { id: 18, question: "How to upgrade, downgrade, or cancel", answer: "You can manage your subscription through your account settings. Upgrades take effect immediately, while downgrades apply at the next billing cycle. Cancellations can be done at any time.", category: "subscription" },
    { id: 19, question: "Payment methods and security", answer: "We accept major credit cards and process payments securely through third-party providers. All payment information is encrypted and never stored on our servers.", category: "subscription" },
    { id: 20, question: "Dashboard overview and analytics", answer: "The dashboard shows real-time portfolio allocations, performance charts comparing Altradenative's strategy to the S&P 500, and historical backtested data spanning 20 years.", category: "platform" },
    { id: 21, question: "Performance metrics explained", answer: "Key metrics include: % of Years Outperforming SPY, Average Yearly Return, Volatility, Risk-Return Ratio, and Maximum Yearly Drawdown. These help you understand the strategy's historical performance.", category: "platform" },
    { id: 22, question: "Backtesting and historical data", answer: "Our algorithm has been backtested over 20 years of historical data. This includes performance during various market conditions, including bull markets, bear markets, and periods of high volatility.", category: "platform" },
    { id: 23, question: "Data protection measures", answer: "We use industry-standard encryption, secure servers, and regular security audits. Your personal information is protected according to GDPR, CCPA, and other applicable data protection regulations.", category: "security" },
    { id: 24, question: "International data transfers", answer: "As a global platform, your data may be processed in countries outside your own. We ensure appropriate safeguards are in place and comply with applicable data protection laws.", category: "security" },
    { id: 25, question: "Your rights regarding personal data", answer: "You have the right to access, correct, delete, or export your personal data. You can also object to certain processing activities. Contact us at <a href='mailto:contact@hedgit.ai'>contact@hedgit.ai</a> to exercise these rights.", category: "security" },
    { id: 26, question: "How the algorithm works", answer: "The algorithm continuously monitors major global indices, tracking price action and volatility. It dynamically adjusts portfolio allocations between core ETFs, leveraged ETFs, and risk-free assets based on market conditions.", category: "investment" },
    { id: 27, question: "What indices and assets are covered", answer: "We cover S&P 500 (SPY/VOO), Nasdaq 100 (QQQ), leveraged versions (UPRO, TQQQ), and risk-free assets (cash, T-bills). Three dashboard options are available: Global Markets, Global Technology, and Hybrid.", category: "investment" },
    { id: 28, question: "Risk management philosophy", answer: "Our philosophy follows classic investment wisdom: be opportunistic when others are fearful and cautious when they are greedy. The algorithm increases market exposure during identified opportunities and reduces it when elevated risk is detected.", category: "investment" },
    { id: 29, question: "Terms of Service summary", answer: "By using Altradenative, you agree to our Terms of Service. Key points: we provide analytics only (not trading advice), you must be 18+, subscriptions can be canceled anytime, and all investing carries risk. <a href='#' class='faq-modal-link' data-modal='terms'>Read full Terms</a>.", category: "legal" },
    { id: 30, question: "Privacy Policy highlights", answer: "We collect minimal personal information, use it only to provide services, don't sell your data, and comply with major data protection laws. <a href='#' class='faq-modal-link' data-modal='privacy'>Read full Privacy Policy</a>.", category: "legal" },
    { id: 31, question: "Disclaimers and limitations", answer: "Altradenative does not guarantee investment results. Past performance doesn't guarantee future results. We're not a broker or advisor. You're solely responsible for your investment decisions. All investing carries risk of capital loss.", category: "legal" },
    { id: 32, question: "Access issues", answer: "If you're having trouble accessing your dashboard, try clearing your browser cache, checking your internet connection, or logging out and back in. If problems persist, contact <a href='mailto:contact@hedgit.ai'>contact@hedgit.ai</a>.", category: "troubleshooting" },
    { id: 33, question: "Dashboard not updating", answer: "Dashboard data updates daily. If you notice stale data, refresh the page. For persistent issues, check your subscription status or contact support.", category: "troubleshooting" },
    { id: 34, question: "Subscription problems", answer: "If you're experiencing billing issues, access problems after payment, or subscription management difficulties, please contact <a href='mailto:contact@hedgit.ai'>contact@hedgit.ai</a> with your account details.", category: "troubleshooting" },
    { id: 35, question: "How to reach customer service", answer: "Email us at <a href='mailto:contact@hedgit.ai'>contact@hedgit.ai</a>. We typically respond within 24-48 hours during business days.", category: "contact" },
    { id: 36, question: "Reporting technical issues", answer: "If you encounter bugs, errors, or technical problems, please email <a href='mailto:contact@hedgit.ai'>contact@hedgit.ai</a> with details about the issue, your browser, and any error messages you see.", category: "contact" },
    { id: 37, question: "Feedback and suggestions", answer: "We welcome your feedback! Send suggestions, feature requests, or general comments to <a href='mailto:contact@hedgit.ai'>contact@hedgit.ai</a>. Your input helps us improve the platform.", category: "contact" }
  ];

  // Generate dates for chart
  const generateDates = () => {
    const dates = [];
    const today = new Date();
    for (let i = 11; i >= 0; i--) {
      const date = new Date(today);
      date.setMonth(date.getMonth() - i);
      dates.push(date.toLocaleDateString('en-US', { month: 'short', year: '2-digit' }));
    }
    return dates;
  };

  // Hex to RGB helper
  const hexToRgb = (hex) => {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    return result ? 
      `${parseInt(result[1], 16)}, ${parseInt(result[2], 16)}, ${parseInt(result[3], 16)}` : 
      '73, 169, 255';
  };

  // Initialize performance chart - separate initialization from updates
  useEffect(() => {
    if (!performanceChartRef.current || performanceChartInstanceRef.current) return;

    let resizeHandler = null;

    // Small delay to ensure DOM is ready
    const initChart = () => {
      const chartDom = performanceChartRef.current;
      if (!chartDom) return false;

      // If dimensions aren't ready, wait a bit
      if (chartDom.offsetWidth === 0 || chartDom.offsetHeight === 0) {
        setTimeout(initChart, 50);
        return false;
      }

      // Initialize chart only if it doesn't exist
      if (!performanceChartInstanceRef.current) {
        performanceChartInstanceRef.current = echarts.init(chartDom, null, {
          renderer: 'canvas',
          useDirtyRect: false
        });
        
        // Add resize handler
        resizeHandler = () => {
          if (performanceChartInstanceRef.current) {
            performanceChartInstanceRef.current.resize();
          }
        };
        window.addEventListener('resize', resizeHandler);
        return true;
      }
      return false;
    };

    const initTimer = setTimeout(() => {
      initChart();
    }, 100);

    // Cleanup function - only dispose on unmount
    return () => {
      clearTimeout(initTimer);
      if (resizeHandler) {
        window.removeEventListener('resize', resizeHandler);
      }
      // Only dispose on component unmount, not on riskProfile change
      // This is handled in a separate effect cleanup
    };
  }, []); // Empty dependency array - only run once on mount

  // Helper function to build chart option
  const buildChartOption = (data, dates, accentColor) => {
    const option = {
      backgroundColor: 'transparent',
      animation: true,
      animationDuration: 1500,
      animationEasing: 'cubicOut',
      grid: {
        left: '3%',
        right: '4%',
        bottom: '12%',
        top: '10%',
        containLabel: true
      },
      xAxis: {
        type: 'category',
        boundaryGap: false,
        data: dates,
        axisLine: {
          show: true,
          lineStyle: {
            color: 'rgba(255, 255, 255, 0.1)',
            width: 1
          }
        },
        axisLabel: {
          color: 'rgba(150, 164, 189, 0.6)',
          fontSize: 11,
          fontFamily: 'Montserrat, Roboto, sans-serif',
          fontWeight: 400,
          letterSpacing: '0.08em',
          rotate: 0,
          show: true,
          interval: (index, value) => {
            // Show every other label to avoid crowding
            return index % 2 === 0;
          }
        },
        axisTick: {
          show: false
        }
      },
      yAxis: {
        type: 'value',
        scale: true,
        axisLine: {
          show: false
        },
        axisLabel: {
          color: 'rgba(150, 164, 189, 0.6)',
          fontSize: 11,
          fontFamily: 'Montserrat, Roboto, sans-serif',
          fontWeight: 400,
          formatter: '{value}%'
        },
        splitLine: {
          show: true,
          lineStyle: {
            color: 'rgba(255, 255, 255, 0.05)',
            type: 'solid',
            width: 1
          }
        }
      },
      tooltip: {
        trigger: 'axis',
        backgroundColor: 'rgba(22, 27, 35, 0.95)',
        borderColor: 'rgba(255, 255, 255, 0.1)',
        borderWidth: 1,
        textStyle: {
          color: '#f4f7fb',
          fontFamily: 'Montserrat, Roboto, sans-serif',
          fontSize: 12
        },
        axisPointer: {
          type: 'cross',
          crossStyle: {
            color: accentColor,
            opacity: 0.3
          },
          lineStyle: {
            color: accentColor,
            width: 1,
            type: 'dashed'
          }
        },
        formatter: (params) => {
          let result = `<div style="padding: 4px 0; font-weight: 600; letter-spacing: 0.1em;">${params[0].axisValue}</div>`;
          params.forEach((item) => {
            const color = item.color;
            const value = item.value.toFixed(1);
            const seriesName = item.seriesName;
            result += `<div style="padding: 2px 0;">
              <span style="display: inline-block; width: 10px; height: 10px; border-radius: 50%; background: ${color}; margin-right: 8px;"></span>
              <span style="color: rgba(150, 164, 189, 0.9);">${seriesName}: </span>
              <span style="color: ${color}; font-weight: 600;">${value}%</span>
            </div>`;
          });
          return result;
        }
      },
      legend: {
        show: false
      },
      series: [
        {
          name: t('landing.hero.portfolio.sp500Legend'),
          type: 'line',
          smooth: true,
          symbol: 'circle',
          symbolSize: 0,
          showSymbol: true,
          lineStyle: {
            width: 2.5,
            color: '#96a4bd',
            shadowBlur: 8,
            shadowColor: 'rgba(150, 164, 189, 0.28)'
          },
          itemStyle: {
            color: '#96a4bd'
          },
          areaStyle: {
            color: {
              type: 'linear',
              x: 0,
              y: 0,
              x2: 0,
              y2: 1,
              colorStops: [
                { offset: 0, color: 'rgba(150, 164, 189, 0.45)' },
                { offset: 1, color: 'rgba(150, 164, 189, 0)' }
              ]
            }
          },
          emphasis: {
            focus: 'series',
            lineStyle: {
              width: 3
            },
            symbolSize: 6,
            itemStyle: {
              borderColor: '#fff',
              borderWidth: 2,
              shadowBlur: 8,
              shadowColor: 'rgba(150, 164, 189, 0.5)'
            }
          },
          data: data.sp500,
          animationDelay: (idx) => idx * 50,
          animationDuration: 1500,
          animationEasing: 'cubicOut'
        },
        {
          name: t('landing.hero.portfolio.portfolioLegend'),
          type: 'line',
          smooth: true,
          symbol: 'circle',
          symbolSize: 0,
          showSymbol: true,
          lineStyle: {
            width: 3,
            color: accentColor,
            shadowBlur: 12,
            shadowColor: `rgba(${hexToRgb(accentColor)}, 0.32)`
          },
          itemStyle: {
            color: accentColor
          },
          areaStyle: {
            color: {
              type: 'linear',
              x: 0,
              y: 0,
              x2: 0,
              y2: 1,
              colorStops: [
                { offset: 0, color: `rgba(${hexToRgb(accentColor)}, 0.55)` },
                { offset: 1, color: `rgba(${hexToRgb(accentColor)}, 0)` }
              ]
            }
          },
          emphasis: {
            focus: 'series',
            lineStyle: {
              width: 3.5
            },
            symbolSize: 7,
            itemStyle: {
              borderColor: '#fff',
              borderWidth: 2,
              shadowBlur: 10,
              shadowColor: accentColor
            }
          },
          data: data.portfolio,
          animationDelay: (idx) => idx * 50 + 100,
          animationDuration: 1500,
          animationEasing: 'cubicOut'
        }
      ]
    };
    return option;
  };

  // Update chart when risk profile changes or on initial mount
  useEffect(() => {
    if (!performanceChartRef.current) return;

    const updateChart = () => {
      // Initialize chart if it doesn't exist
      if (!performanceChartInstanceRef.current) {
        const chartDom = performanceChartRef.current;
        if (!chartDom || chartDom.offsetWidth === 0 || chartDom.offsetHeight === 0) {
          // Retry after a short delay if dimensions aren't ready
          setTimeout(updateChart, 50);
          return;
        }

        performanceChartInstanceRef.current = echarts.init(chartDom, null, {
          renderer: 'canvas',
          useDirtyRect: false
        });

        // Add resize handler once
        const resizeHandler = () => {
          if (performanceChartInstanceRef.current) {
            performanceChartInstanceRef.current.resize();
          }
        };
        window.addEventListener('resize', resizeHandler);
      }

      // Update chart with current data
      if (performanceChartInstanceRef.current) {
        const data = performanceData[riskProfile];
        const dates = generateDates();
        const accentColor = profileThemes[riskProfile].accent;
        const option = buildChartOption(data, dates, accentColor);

        // Use setOption with true (notMerge) to force full replacement and trigger animations
        // Match the original HTML implementation exactly
        performanceChartInstanceRef.current.setOption(option, true);
      }
    };

    // Small delay to ensure DOM is ready
    const timer = setTimeout(updateChart, 100);
    return () => clearTimeout(timer);
  }, [riskProfile, t]); // Update when riskProfile or language changes

  // Cleanup - dispose chart only on component unmount
  useEffect(() => {
    return () => {
      if (performanceChartInstanceRef.current) {
        performanceChartInstanceRef.current.dispose();
        performanceChartInstanceRef.current = null;
      }
    };
  }, []); // Only on unmount

  // Helper function to build proof chart option
  const buildProofChartOption = (data, accentColor) => {
    return {
      backgroundColor: 'transparent',
      animation: true,
      animationDuration: 1000,
      animationEasing: 'cubicOut',
      grid: {
        left: '10%',
        right: '8%',
        top: '15%',
        bottom: '15%',
        containLabel: true
      },
      xAxis: {
        type: 'category',
        data: proofData.years,
        axisLine: {
          lineStyle: {
            color: 'rgba(255, 255, 255, 0.1)'
          }
        },
        axisLabel: {
          color: 'rgba(150, 164, 189, 0.8)',
          fontFamily: 'Montserrat, Roboto, sans-serif',
          fontSize: 11,
          rotate: 45
        },
        axisTick: {
          show: false
        }
      },
      yAxis: {
        type: 'value',
        name: 'Return %',
        nameLocation: 'middle',
        nameGap: 50,
        nameTextStyle: {
          color: 'rgba(150, 164, 189, 0.8)',
          fontFamily: 'Montserrat, Roboto, sans-serif',
          fontSize: 12
        },
        axisLine: {
          lineStyle: {
            color: 'rgba(255, 255, 255, 0.1)'
          }
        },
        axisLabel: {
          color: 'rgba(150, 164, 189, 0.8)',
          fontFamily: 'Montserrat, Roboto, sans-serif',
          fontSize: 11,
          formatter: '{value}%'
        },
        splitLine: {
          lineStyle: {
            color: 'rgba(255, 255, 255, 0.05)',
            type: 'dashed'
          }
        }
      },
      tooltip: {
        trigger: 'axis',
        backgroundColor: 'rgba(22, 27, 35, 0.95)',
        borderColor: 'rgba(255, 255, 255, 0.1)',
        borderWidth: 1,
        textStyle: {
          color: '#f4f7fb',
          fontFamily: 'Montserrat, Roboto, sans-serif',
          fontSize: 12
        },
        formatter: (params) => {
          let result = `<div style="padding: 4px 0; font-weight: 600; letter-spacing: 0.1em;">${params[0].axisValue}</div>`;
          params.forEach((item) => {
            const color = item.color;
            const value = item.value.toFixed(1);
            const seriesName = item.seriesName;
            result += `<div style="padding: 2px 0;">
              <span style="display: inline-block; width: 10px; height: 10px; border-radius: 50%; background: ${color}; margin-right: 8px;"></span>
              <span style="color: rgba(150, 164, 189, 0.9);">${seriesName}: </span>
              <span style="color: ${color}; font-weight: 600;">${value}%</span>
            </div>`;
          });
          return result;
        }
      },
      legend: {
        show: true,
        bottom: 10,
        textStyle: {
          color: 'rgba(150, 164, 189, 0.9)',
          fontFamily: 'Montserrat, Roboto, sans-serif',
          fontSize: 12
        },
        itemGap: 20
      },
      series: [
        {
          name: t('landing.hero.portfolio.sp500Legend'),
          type: 'bar',
          data: proofData.spy,
          itemStyle: {
            color: '#96a4bd',
            borderRadius: [4, 4, 0, 0]
          },
          emphasis: {
            itemStyle: {
              shadowBlur: 10,
              shadowColor: 'rgba(150, 164, 189, 0.5)'
            }
          }
        },
        {
          name: t('landing.hero.portfolio.portfolioLegend'),
          type: 'bar',
          data: data.data,
          itemStyle: {
            color: accentColor,
            borderRadius: [4, 4, 0, 0]
          },
          emphasis: {
            itemStyle: {
              shadowBlur: 10,
              shadowColor: accentColor
            }
          }
        }
      ]
    };
  };

  // Initialize proof chart - separate initialization from updates
  useEffect(() => {
    if (!proofChartRef.current || proofChartInstanceRef.current) return;

    let resizeHandler = null;

    // Small delay to ensure DOM is ready
    const initChart = () => {
      const chartDom = proofChartRef.current;
      if (!chartDom) return false;

      // If dimensions aren't ready, wait a bit
      if (chartDom.offsetWidth === 0 || chartDom.offsetHeight === 0) {
        setTimeout(initChart, 50);
        return false;
      }

      // Initialize chart only if it doesn't exist
      if (!proofChartInstanceRef.current) {
        proofChartInstanceRef.current = echarts.init(chartDom, null, {
          renderer: 'canvas',
          useDirtyRect: false
        });
        
        // Set initial data immediately after initialization
        const initialData = proofData[proofRiskProfile];
        if (initialData) {
          const initialAccentColor = profileThemes[proofRiskProfile].accent;
          const initialOption = buildProofChartOption(initialData, initialAccentColor);
          proofChartInstanceRef.current.setOption(initialOption);
        }
        
        // Add resize handler
        resizeHandler = () => {
          if (proofChartInstanceRef.current) {
            proofChartInstanceRef.current.resize();
          }
        };
        window.addEventListener('resize', resizeHandler);
        return true;
      }
      return false;
    };

    const initTimer = setTimeout(() => {
      initChart();
    }, 100);

    // Cleanup function - only dispose on unmount
    return () => {
      clearTimeout(initTimer);
      if (resizeHandler) {
        window.removeEventListener('resize', resizeHandler);
      }
    };
  }, []); // Empty dependency array - only run once on mount

  // Update proof chart when risk profile changes or on initial mount
  useEffect(() => {
    if (!proofChartRef.current || !proofData || !proofData.years) return;

    const updateChart = () => {
      // Initialize chart if it doesn't exist
      if (!proofChartInstanceRef.current) {
        const chartDom = proofChartRef.current;
        if (!chartDom || chartDom.offsetWidth === 0 || chartDom.offsetHeight === 0) {
          // Retry after a short delay if dimensions aren't ready
          setTimeout(updateChart, 50);
          return;
        }

        proofChartInstanceRef.current = echarts.init(chartDom, null, {
          renderer: 'canvas',
          useDirtyRect: false
        });

        // Add resize handler once
        const resizeHandler = () => {
          if (proofChartInstanceRef.current) {
            proofChartInstanceRef.current.resize();
          }
        };
        window.addEventListener('resize', resizeHandler);
      }

      // Update chart with current data
      if (proofChartInstanceRef.current) {
        const data = proofData[proofRiskProfile];
        if (!data) return;

        const accentColor = profileThemes[proofRiskProfile].accent;
        const option = buildProofChartOption(data, accentColor);

        // Use setOption with true (notMerge) to force full replacement and trigger animations
        // Match the original HTML implementation exactly
        proofChartInstanceRef.current.setOption(option, true);
      }
    };

    // Small delay to ensure DOM is ready
    const timer = setTimeout(updateChart, 100);
    return () => clearTimeout(timer);
  }, [proofRiskProfile, t]); // Update when proofRiskProfile or language changes

  // Cleanup - dispose proof chart only on component unmount
  useEffect(() => {
    return () => {
      if (proofChartInstanceRef.current) {
        proofChartInstanceRef.current.dispose();
        proofChartInstanceRef.current = null;
      }
    };
  }, []); // Only on unmount

  // Get current allocation data (defined early for use in effects)
  const currentAllocation = allocationData[riskProfile] || { core: 80, leveraged: 0, riskfree: 20 };

  // Update CSS variables and visual effects when risk profile changes
  useEffect(() => {
    const theme = profileThemes[riskProfile];
    if (theme) {
      document.documentElement.style.setProperty('--accent', theme.accent);
      document.documentElement.style.setProperty('--risk-toggle-shadow', theme.shadow);
      document.documentElement.style.setProperty('--risk-toggle-pill', theme.gradientA);
      document.documentElement.style.setProperty('--panel-orbit-a', theme.orbitA || theme.gradientA);
      document.documentElement.style.setProperty('--panel-orbit-b', theme.orbitB || theme.gradientB);
      
      // Update hero-content glow effect
      const heroContent = document.querySelector('.hero-content');
      if (heroContent) {
        heroContent.style.boxShadow = `0 40px 120px rgba(0, 0, 0, 0.4), 0 0 80px ${theme.shadow}`;
        heroContent.style.transition = 'box-shadow 0.6s cubic-bezier(0.4, 0, 0.2, 1)';
      }
      
      // Update chart inner glow
      const chartInnerGlow = document.querySelector('.chart-inner-glow');
      if (chartInnerGlow) {
        const rgb = hexToRgb(theme.accent);
        chartInnerGlow.style.background = `radial-gradient(circle, rgba(${rgb}, 0.22), transparent 72%)`;
        chartInnerGlow.style.transition = 'background 0.6s ease';
      }
    }
  }, [riskProfile]);

  // Badge animation helper
  const easeOutCubic = (t) => 1 - Math.pow(1 - t, 3);

  const animateBadge = (badge, valueElement, startValue, endValue, duration = 900) => {
    const startTime = performance.now();

    const step = (now) => {
      const progress = Math.min((now - startTime) / duration, 1);
      const eased = easeOutCubic(progress);
      const value = startValue + (endValue - startValue) * eased;

      badge.style.setProperty('--badge-fill', value);
      if (valueElement) {
        valueElement.textContent = `${Math.round(value)}%`;
      }

      if (progress < 1) {
        requestAnimationFrame(step);
      } else {
        badge.style.setProperty('--badge-fill', endValue);
        if (valueElement) {
          valueElement.textContent = `${Math.round(endValue)}%`;
        }
        badge.dataset.value = String(endValue);
      }
    };

    requestAnimationFrame(step);
  };

  // Initialize badges with proper styles and animations
  useEffect(() => {
    const allocation = allocationData[riskProfile] || { core: 80, leveraged: 0, riskfree: 20 };
    const allocationItems = document.querySelectorAll('.allocation-item');
    
    allocationItems.forEach((item) => {
      const badge = item.querySelector('.badge');
      if (badge) {
        const key = item.getAttribute('data-key');
        const valueElement = badge.querySelector('span');
        const endValue = allocation[key] || 0;
        const startValue = parseFloat(badge.dataset.value || badge.style.getPropertyValue('--badge-fill') || '0');
        
        badge.classList.toggle('is-zero', endValue <= 0.5);
        
        // Animate badge value change
        if (startValue !== endValue) {
          animateBadge(badge, valueElement, startValue, endValue);
        } else {
          badge.style.setProperty('--badge-fill', endValue);
          if (valueElement) {
            valueElement.textContent = `${Math.round(endValue)}%`;
          }
          badge.dataset.value = String(endValue);
        }
      }
    });
  }, [riskProfile]);

  // Scroll handler for top bar
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Initialize scroll-triggered animations for all sections
  useEffect(() => {
    // Problem section animations
    const initProblemSectionAnimations = () => {
      const problemSection = document.querySelector('.problem-section');
      if (!problemSection) return;

      const observerOptions = {
        threshold: 0.2,
        rootMargin: '0px 0px -100px 0px'
      };

      const observer = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            problemSection.classList.add('visible');
            
            // Animate headline top
            const headlineTop = problemSection.querySelector('.problem-headline-top');
            if (headlineTop) {
              headlineTop.style.animation = 'problemHeadlineFadeIn 0.9s cubic-bezier(0.4, 0, 0.2, 1) forwards';
            }
            
            // Animate headline bottom with slight delay
            const headlineBottom = problemSection.querySelector('.problem-headline-bottom');
            if (headlineBottom) {
              headlineBottom.style.animation = 'problemHeadlineFadeIn 0.9s cubic-bezier(0.4, 0, 0.2, 1) 0.15s forwards';
            }
            
            // Animate subheadline
            const subheadline = problemSection.querySelector('.problem-subheadline');
            if (subheadline) {
              subheadline.style.animation = 'problemFadeInUp 0.8s cubic-bezier(0.4, 0, 0.2, 1) 0.35s forwards';
            }
            
            // Animate items with stagger and bounce icons
            const items = problemSection.querySelectorAll('.problem-item');
            items.forEach((item, index) => {
              const delay = 0.55 + index * 0.1;
              item.style.animation = `problemFadeInUp 0.8s cubic-bezier(0.4, 0, 0.2, 1) ${delay}s forwards`;
              
              // Animate icons separately with bounce effect
              const icon = item.querySelector('.problem-icon');
              if (icon) {
                icon.style.animation = `iconBounceIn 0.7s cubic-bezier(0.34, 1.56, 0.64, 1) ${delay + 0.1}s forwards`;
                
                // Add 'animated' class after animation completes to maintain glow effect
                setTimeout(() => {
                  icon.classList.add('animated');
                }, (delay + 0.8) * 1000);
              }
              
              // Animate text content after icon with a slight delay
              const text = item.querySelector('.problem-text');
              if (text) {
                setTimeout(() => {
                  text.classList.add('visible');
                }, (delay + 0.25) * 1000);
              }
            });
            
            observer.unobserve(problemSection);
          }
        });
      }, observerOptions);

      observer.observe(problemSection);
    };

    // Solution section animations
    const initSolutionSectionAnimations = () => {
      const solutionSection = document.querySelector('.solution-section');
      if (!solutionSection) return;

      const observerOptions = {
        threshold: 0.2,
        rootMargin: '0px 0px -100px 0px'
      };

      const observer = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            solutionSection.classList.add('visible');
            
            // Animate logo first (appears immediately)
            const logo = solutionSection.querySelector('.solution-headline-top');
            if (logo) {
              logo.style.animation = 'logoFadeInScale 0.9s cubic-bezier(0.4, 0, 0.2, 1) forwards';
            }
            
            // Animate headline
            const headline = solutionSection.querySelector('.solution-headline');
            if (headline) {
              headline.style.animation = 'solutionFadeInUp 0.8s cubic-bezier(0.4, 0, 0.2, 1) 0.1s forwards';
            }
            
            // Animate subheadline
            const subheadline = solutionSection.querySelector('.solution-subheadline');
            if (subheadline) {
              subheadline.style.animation = 'solutionFadeInUp 0.8s cubic-bezier(0.4, 0, 0.2, 1) 0.3s forwards';
            }
            
            // Animate steps with stagger
            const steps = solutionSection.querySelectorAll('.solution-step');
            steps.forEach((step, index) => {
              step.style.animation = `solutionFadeInUp 0.8s cubic-bezier(0.4, 0, 0.2, 1) ${0.4 + index * 0.15}s forwards`;
            });
            
            // Animate blockquote after steps
            const quote = solutionSection.querySelector('.solution-quote');
            if (quote) {
              quote.style.animation = `solutionFadeInUp 0.8s cubic-bezier(0.4, 0, 0.2, 1) ${0.85}s forwards`;
            }
            
            observer.unobserve(solutionSection);
          }
        });
      }, observerOptions);

      observer.observe(solutionSection);
    };

    // Proof section animations
    const initProofSectionAnimations = () => {
      const proofSection = document.querySelector('.proof-section');
      if (!proofSection) return;

      const observerOptions = {
        threshold: 0.2,
        rootMargin: '0px 0px -100px 0px'
      };

      const observer = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            proofSection.classList.add('visible');
            
            // Animate headline top
            const headlineTop = proofSection.querySelector('.proof-headline-top');
            if (headlineTop) {
              headlineTop.style.animation = 'proofHeadlineFadeIn 0.9s cubic-bezier(0.4, 0, 0.2, 1) forwards';
            }
            
            // Animate headline bottom with slight delay
            const headlineBottom = proofSection.querySelector('.proof-headline-bottom');
            if (headlineBottom) {
              headlineBottom.style.animation = 'proofHeadlineFadeIn 0.9s cubic-bezier(0.4, 0, 0.2, 1) 0.15s forwards';
            }
            
            // Animate chart container
            const chartContainer = proofSection.querySelector('.proof-chart-container');
            if (chartContainer) {
              chartContainer.style.animation = 'proofFadeInUp 0.8s cubic-bezier(0.4, 0, 0.2, 1) 0.35s forwards';
            }
            
            // Animate metrics container
            const metricsContainer = proofSection.querySelector('.proof-metrics');
            if (metricsContainer) {
              metricsContainer.style.animation = 'proofFadeInUp 0.8s cubic-bezier(0.4, 0, 0.2, 1) 0.5s forwards';
            }
            
            // Animate individual metric cards with stagger
            const metricCards = proofSection.querySelectorAll('.metric-card');
            metricCards.forEach((card, index) => {
              const cardDelay = 0.65 + index * 0.08;
              card.style.animation = `metricCardFadeIn 0.7s cubic-bezier(0.4, 0, 0.2, 1) ${cardDelay}s forwards`;
              
              // Mark card as animated after animation completes
              setTimeout(() => {
                card.classList.add('animated');
              }, (cardDelay + 0.7) * 1000);
              
              // Animate label and value within each card
              const label = card.querySelector('.metric-label');
              const value = card.querySelector('.metric-value');
              
              if (label) {
                setTimeout(() => {
                  label.style.animation = 'slideInLeft 0.5s cubic-bezier(0.4, 0, 0.2, 1) forwards';
                }, (cardDelay + 0.2) * 1000);
              }
              
              if (value) {
                setTimeout(() => {
                  value.style.animation = 'slideInRight 0.5s cubic-bezier(0.4, 0, 0.2, 1) forwards';
                }, (cardDelay + 0.3) * 1000);
              }
            });
            
            // Animate pillars container
            const pillarsContainer = proofSection.querySelector('.proof-pillars');
            if (pillarsContainer) {
              pillarsContainer.style.animation = 'proofFadeInUp 0.8s cubic-bezier(0.4, 0, 0.2, 1) 1.3s forwards';
            }
            
            // Animate individual pillar cards with stagger
            const pillarCards = proofSection.querySelectorAll('.pillar-card');
            pillarCards.forEach((card, index) => {
              const pillarDelay = 1.45 + index * 0.12;
              card.style.animation = `proofFadeInUp 0.7s cubic-bezier(0.4, 0, 0.2, 1) ${pillarDelay}s forwards`;
              
              // Animate title and description within each pillar
              const title = card.querySelector('.pillar-title');
              const description = card.querySelector('.pillar-description');
              
              if (title) {
                setTimeout(() => {
                  title.style.animation = 'pillarFadeIn 0.6s cubic-bezier(0.4, 0, 0.2, 1) forwards';
                }, (pillarDelay + 0.2) * 1000);
              }
              
              if (description) {
                setTimeout(() => {
                  description.style.animation = 'pillarFadeIn 0.6s cubic-bezier(0.4, 0, 0.2, 1) forwards';
                }, (pillarDelay + 0.35) * 1000);
              }
            });
            
            observer.unobserve(proofSection);
          }
        });
      }, observerOptions);

      observer.observe(proofSection);
    };

    // Product section animations
    const initProductSectionAnimations = () => {
      const productSection = document.querySelector('.product-section');
      if (!productSection) return;

      const observerOptions = {
        threshold: 0.2,
        rootMargin: '0px 0px -100px 0px'
      };

      const observer = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            productSection.classList.add('visible');
            observer.unobserve(productSection);
          }
        });
      }, observerOptions);

      observer.observe(productSection);
    };

    // How It Works section animations
    const initHowItWorksSectionAnimations = () => {
      const howItWorksSection = document.querySelector('.how-it-works-section');
      if (!howItWorksSection) return;

      const observerOptions = {
        threshold: 0.2,
        rootMargin: '0px 0px -100px 0px'
      };

      const observer = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            howItWorksSection.classList.add('visible');
            observer.unobserve(howItWorksSection);
          }
        });
      }, observerOptions);

      observer.observe(howItWorksSection);
    };

    // CTA section animations
    const initCTASectionAnimations = () => {
      const ctaSection = document.querySelector('.cta-section');
      if (!ctaSection) return;

      const observerOptions = {
        threshold: 0.2,
        rootMargin: '0px 0px -100px 0px'
      };

      const observer = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            ctaSection.classList.add('visible');
            observer.unobserve(ctaSection);
          }
        });
      }, observerOptions);

      observer.observe(ctaSection);
    };

    // How Altradenative Works section animations
    const initHowAltradenativeWorksSectionAnimations = () => {
      const howAltradenativeWorksSection = document.querySelector('.how-altradenative-works-section');
      if (!howAltradenativeWorksSection) return;

      const observerOptions = {
        threshold: 0.2,
        rootMargin: '0px 0px -100px 0px'
      };

      const observer = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            howAltradenativeWorksSection.classList.add('visible');
            
            // Animate headline top
            const headlineTop = howAltradenativeWorksSection.querySelector('.how-altradenative-works-headline-top');
            if (headlineTop) {
              headlineTop.style.animation = 'problemHeadlineFadeIn 0.9s cubic-bezier(0.4, 0, 0.2, 1) forwards';
            }
            
            // Animate headline bottom with slight delay
            const headlineBottom = howAltradenativeWorksSection.querySelector('.how-altradenative-works-headline-bottom');
            if (headlineBottom) {
              headlineBottom.style.animation = 'problemHeadlineFadeIn 0.9s cubic-bezier(0.4, 0, 0.2, 1) 0.15s forwards';
            }
            
            // Animate subheadline
            const subheadline = howAltradenativeWorksSection.querySelector('.how-altradenative-works-subheadline');
            if (subheadline) {
              subheadline.style.animation = 'problemFadeInUp 0.8s cubic-bezier(0.4, 0, 0.2, 1) 0.35s forwards';
            }
            
            // Animate concept cards with stagger
            const conceptCards = howAltradenativeWorksSection.querySelectorAll('.how-altradenative-works-concept-card');
            conceptCards.forEach((card, index) => {
              const delay = 0.55 + index * 0.1;
              card.style.animation = `problemFadeInUp 0.8s cubic-bezier(0.4, 0, 0.2, 1) ${delay}s forwards`;
              
              // Animate icons separately with bounce effect
              const icon = card.querySelector('.how-altradenative-works-concept-icon');
              if (icon) {
                icon.style.animation = `iconBounceIn 0.7s cubic-bezier(0.34, 1.56, 0.64, 1) ${delay + 0.1}s forwards`;
                
                // Add 'animated' class after animation completes
                setTimeout(() => {
                  icon.classList.add('animated');
                }, (delay + 0.8) * 1000);
              }
            });
            
            observer.unobserve(howAltradenativeWorksSection);
          }
        });
      }, observerOptions);

      observer.observe(howAltradenativeWorksSection);
    };

    // Footer section animations
    const initFooterSectionAnimations = () => {
      const footerSection = document.querySelector('.footer-section');
      if (!footerSection) return;

      const observerOptions = {
        threshold: 0.2,
        rootMargin: '0px 0px -50px 0px'
      };

      const observer = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            footerSection.classList.add('visible');
            observer.unobserve(footerSection);
          }
        });
      }, observerOptions);

      observer.observe(footerSection);
    };

    // Initialize all section animations after a short delay to ensure DOM is ready
    const initTimer = setTimeout(() => {
      initHowAltradenativeWorksSectionAnimations();
      initProblemSectionAnimations();
      initSolutionSectionAnimations();
      initProofSectionAnimations();
      initProductSectionAnimations();
      initHowItWorksSectionAnimations();
      initCTASectionAnimations();
      initFooterSectionAnimations();
    }, 200);

    return () => clearTimeout(initTimer);
  }, []);

  // Handle modal open/close
  const handleModalOpen = (modalName) => {
    setOpenModal(modalName);
    document.body.style.overflow = 'hidden';
  };

  const handleModalClose = () => {
    setOpenModal(null);
    document.body.style.overflow = '';
  };

  // Handle ESC key for modals
  useEffect(() => {
    const handleEsc = (e) => {
      if (e.key === 'Escape' && openModal) {
        handleModalClose();
      }
    };

    window.addEventListener('keydown', handleEsc);
    return () => window.removeEventListener('keydown', handleEsc);
  }, [openModal]);

  // Get proof metrics
  const proofMetrics = proofData[proofRiskProfile]?.metrics || proofData.Neutral.metrics;

  // Get excess return value based on profile
  const getExcessReturnValue = (profile) => {
    const value = profile === 'Averse' ? -2 : profile === 'Neutral' ? 5 : 10;
    return value > 0 ? `+${value}%` : `${value}%`;
  };

  return (
    <>
      {/* Fixed Top Navigation Bar */}
      <nav className={`fixed-top-bar ${isScrolled ? 'scrolled' : ''}`}>
        <div className="top-bar-logo">
          <button 
            className="language-toggle" 
            onClick={() => handleLanguageToggle(i18n.language === 'en' ? 'es' : 'en')}
            aria-label={t('landing.topBar.toggleLanguage')}
          >
            <span className={`lang-option ${i18n.language === 'en' ? 'active' : ''}`}>EN</span>
            <span className="lang-separator">|</span>
            <span className={`lang-option ${i18n.language === 'es' ? 'active' : ''}`}>ES</span>
          </button>
          <svg 
            id="Component_13_5" 
            data-name="Component 13 – 5" 
            xmlns="http://www.w3.org/2000/svg" 
            xmlnsXlink="http://www.w3.org/1999/xlink" 
            width="86.609" 
            height="77.109" 
            viewBox="0 0 86.609 77.109"
          >
            <defs>
              <clipPath id="clip-path">
                <rect 
                  id="Rectangle_351" 
                  data-name="Rectangle 351" 
                  width="42.305" 
                  height="48.895" 
                  transform="translate(-3 -6)" 
                  fill="none" 
                  stroke={profileThemes[riskProfile].accent} 
                  strokeLinejoin="round" 
                  strokeWidth="2"
                />
              </clipPath>
            </defs>
            <path 
              id="Path_38578" 
              data-name="Path 38578" 
              d="M31.061,4.859,14.374,37.091.435,65.389c-1.175,2.385.114,4.365,2.866,4.4l27.76.34,27.76-.34c2.752-.034,4.041-2.013,2.866-4.4l-13.94-28.3Z" 
              transform="translate(0 6.982)" 
              fill={profileThemes[riskProfile].accent}
            />
            <g id="Group_11876" data-name="Group 11876" transform="translate(48.422 6)" style={{ isolation: 'isolate' }}>
              <g id="Group_11849" data-name="Group 11849" transform="translate(-1.119 0)">
                <g id="Group_11848" data-name="Group 11848" clipPath="url(#clip-path)">
                  <path 
                    id="Path_38576" 
                    data-name="Path 38576" 
                    d="M45.006,46.045,54.8,27.824l8.179-15.995a1.754,1.754,0,0,0-1.683-2.488L45.006,9.15l-16.289.191a1.754,1.754,0,0,0-1.681,2.488l8.179,15.995Z" 
                    transform="translate(-26.854 -9.149)" 
                    fill="none" 
                    stroke={profileThemes[riskProfile].accent} 
                    strokeLinejoin="round" 
                    strokeWidth="2"
                  />
                </g>
              </g>
            </g>
          </svg>
        </div>
        <div className="top-bar-buttons">
          <Link to="/investors" className="top-bar-button tertiary">
            {t('landing.topBar.investors')}
          </Link>
          <Link to="/signin" className="top-bar-button secondary">
            {t('landing.topBar.login')}
          </Link>
          <Link to="/subscriptions" className="top-bar-button primary">
            {t('landing.topBar.getStarted')}
          </Link>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="hero">
        <div className="hero-content">
          <header className="hero-header">
            <div className="hero-subhead">
              <strong>
                <span>{t('landing.hero.subhead.part1')}</span>{' '}
                <span>{t('landing.hero.subhead.part2')}</span>
              </strong>{' '}
              <span>{t('landing.hero.subhead.part3')}</span>{' '}
              <span>{t('landing.hero.subhead.part4')}</span>
            </div>
            <div className="brand-area">
              <div className="brand-name">
                <img src="/images/logos/Logo-Altradenative.png" alt="Altradenative logo" className="brand-logo" />
                <p className="brand-tagline">{t('landing.hero.tagline')}</p>
              </div>
            </div>
          </header>

          <div className="risk-profile-section">
            <div className="risk-profile-label">{t('landing.hero.riskProfileLabel')}</div>
            <div 
              className="risk-toggle" 
              role="tablist" 
              aria-label={t('landing.hero.riskProfileLabel')}
              ref={riskToggleRef}
            >
              <button
                type="button"
                role="tab"
                data-profile="Averse"
                aria-selected={riskProfile === 'Averse'}
                className={riskProfile === 'Averse' ? 'active' : ''}
                onClick={() => setRiskProfile('Averse')}
              >
                {t('landing.hero.riskProfiles.averse')}
              </button>
              <button
                type="button"
                role="tab"
                data-profile="Neutral"
                aria-selected={riskProfile === 'Neutral'}
                className={riskProfile === 'Neutral' ? 'active' : ''}
                onClick={() => setRiskProfile('Neutral')}
              >
                {t('landing.hero.riskProfiles.neutral')}
              </button>
              <button
                type="button"
                role="tab"
                data-profile="Seeker"
                aria-selected={riskProfile === 'Seeker'}
                className={riskProfile === 'Seeker' ? 'active' : ''}
                onClick={() => setRiskProfile('Seeker')}
              >
                {t('landing.hero.riskProfiles.seeker')}
              </button>
            </div>
          </div>

          <div className="dashboard">
            <aside className="panel">
              <h3>{t('landing.hero.portfolio.title')}</h3>
              <p className="panel-subtitle">{t('landing.hero.portfolio.subtitle')}</p>
              <div className="allocations">
                <div className="allocation-item" data-key="core">
                  <div className="badge" style={{ '--badge-fill': currentAllocation.core }}>
                    <span>{currentAllocation.core}%</span>
                  </div>
                  <div className="allocation-copy">
                    <h4>{t('landing.hero.portfolio.core.title')}</h4>
                    <p>{t('landing.hero.portfolio.core.description')}</p>
                  </div>
                </div>
                <div className="allocation-item" data-key="leveraged">
                  <div className={`badge ${currentAllocation.leveraged <= 0.5 ? 'is-zero' : ''}`} style={{ '--badge-fill': currentAllocation.leveraged }}>
                    <span>{currentAllocation.leveraged}%</span>
                  </div>
                  <div className="allocation-copy">
                    <h4>{t('landing.hero.portfolio.leveraged.title')}</h4>
                    <p>{t('landing.hero.portfolio.leveraged.description')}</p>
                  </div>
                </div>
                <div className="allocation-item" data-key="riskfree">
                  <div className="badge" style={{ '--badge-fill': currentAllocation.riskfree }}>
                    <span>{currentAllocation.riskfree}%</span>
                  </div>
                  <div className="allocation-copy">
                    <h4>{t('landing.hero.portfolio.riskfree.title')}</h4>
                    <p>{t('landing.hero.portfolio.riskfree.description')}</p>
                  </div>
                </div>
              </div>
            </aside>

            <article className="panel performance">
              <div className="chart-header">
                <span>{t('landing.hero.portfolio.performance')}</span>
              </div>
              <div className="chart-legend">
                <div className="legend-item">
                  <span className="legend-dot"></span>
                  <span>{t('landing.hero.portfolio.portfolioLegend')}</span>
                </div>
                <div className="legend-item">
                  <span className="legend-dot secondary"></span>
                  <span>{t('landing.hero.portfolio.sp500Legend')}</span>
                </div>
              </div>
              <div className="chart">
                <div className="chart-inner-glow"></div>
                <div 
                  ref={performanceChartRef} 
                  id="performanceChart"
                  style={{ 
                    width: '100%', 
                    height: '100%',
                    position: 'relative',
                    zIndex: 10,
                    minHeight: '320px',
                    padding: '20px 15px 25px',
                    boxSizing: 'border-box'
                  }}
                ></div>
              </div>
            </article>
          </div>
        </div>
      </section>

      {/* How Altradenative Works Section */}
      <section className="how-altradenative-works-section">
        <div className="how-altradenative-works-container">
          <h2 className="how-altradenative-works-headline">
            <span className="how-altradenative-works-headline-top">{t('landing.howAltradenativeWorks.headlineTop')}</span>
            <span className="how-altradenative-works-headline-bottom">{t('landing.howAltradenativeWorks.headlineBottom')}</span>
          </h2>
          <p className="how-altradenative-works-subheadline">{t('landing.howAltradenativeWorks.subheadline')}</p>
          <div className="how-altradenative-works-concepts">
            {t('landing.howAltradenativeWorks.concepts', { returnObjects: true }).map((concept, index) => (
              <div key={index} className="how-altradenative-works-concept-card">
                <div className="how-altradenative-works-concept-content">
                  <div className="how-altradenative-works-concept-icon" aria-hidden="true">
                    <img 
                      src={index === 0 ? '/images/icons/time-to-market.png' : index === 1 ? '/images/icons/asset-management.png' : index === 2 ? '/images/icons/curve.png' : '/images/icons/investment.png'} 
                      alt="" 
                    />
                  </div>
                  <h3 className="how-altradenative-works-concept-title">{concept.title}</h3>
                  <p className="how-altradenative-works-concept-description">{concept.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Problem Section */}
      <section className="problem-section">
        <div className="problem-container">
          <h2 className="problem-headline">
            <span className="problem-headline-top">{t('landing.problem.headlineTop')}</span>
            <span className="problem-headline-bottom">{t('landing.problem.headlineBottom')}</span>
          </h2>
          <p className="problem-subheadline">{t('landing.problem.subheadline')}</p>
          <div className="problem-list">
            {t('landing.problem.items', { returnObjects: true }).map((item, index) => (
              <div key={index} className="problem-item">
                <div className="problem-icon" aria-hidden="true">
                  <img 
                    src={index === 0 ? '/images/icons/hourglass.svg' : index === 1 ? '/images/icons/maze.svg' : index === 2 ? '/images/icons/relativeperformance.svg' : '/images/icons/riskreward.svg'} 
                    alt="" 
                  />
                </div>
                <p className="problem-text">{item.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Solution Section */}
      <section className="solution-section">
        <div className="solution-container">
          <h2 className="solution-headline">
            <div className="solution-headline-top-wrapper">
              <img src="/images/logos/logo.svg" alt="Altradenative" className="solution-headline-top" />
            </div>
            <span className="solution-headline-bottom">{t('landing.solution.headlineBottom')}</span>
          </h2>
          <p className="solution-subheadline">{t('landing.solution.subheadline')}</p>
          
          <div className="solution-steps">
            {t('landing.solution.steps', { returnObjects: true }).map((step, index) => (
              <div key={index} className="solution-step">
                <img 
                  src={index === 0 ? '/images/icons/technical.svg' : index === 1 ? '/images/icons/optimization.svg' : '/images/dashboards/dashbpoard.svg'} 
                  alt={`Step ${index + 1}`} 
                  className="step-number" 
                />
                <div className="step-content">
                  <h3 className="step-title">{step.title}</h3>
                  <p className="step-description">{step.description}</p>
                </div>
              </div>
            ))}
          </div>

          <blockquote className="solution-quote">
            <p>{t('landing.solution.quote')}</p>
          </blockquote>
        </div>
      </section>

      {/* Proof Section */}
      <section className="proof-section">
        <div className="proof-container">
          <h2 className="proof-headline">
            <span className="proof-headline-top">{t('landing.proof.headlineTop')}</span>
            <span 
              className="proof-headline-bottom"
              style={{
                color: profileThemes[proofRiskProfile].accent,
                transition: 'color 0.6s cubic-bezier(0.4, 0, 0.2, 1)'
              }}
            >
              {t('landing.proof.headlineBottom')}
            </span>
          </h2>
          
          <div className="proof-chart-container">
            <div className="proof-toggle-container">
              <div className="risk-profile-label">{t('landing.proof.riskSetupLabel')}</div>
              <div 
                className="risk-toggle proof-toggle" 
                role="tablist" 
                aria-label={t('landing.hero.riskProfileLabel')}
                ref={proofToggleRef}
              >
                <button
                  type="button"
                  role="tab"
                  data-profile="Averse"
                  aria-selected={proofRiskProfile === 'Averse'}
                  className={proofRiskProfile === 'Averse' ? 'active' : ''}
                  onClick={() => setProofRiskProfile('Averse')}
                >
                  {t('landing.hero.riskProfiles.averse')}
                </button>
                <button
                  type="button"
                  role="tab"
                  data-profile="Neutral"
                  aria-selected={proofRiskProfile === 'Neutral'}
                  className={proofRiskProfile === 'Neutral' ? 'active' : ''}
                  onClick={() => setProofRiskProfile('Neutral')}
                >
                  {t('landing.hero.riskProfiles.neutral')}
                </button>
                <button
                  type="button"
                  role="tab"
                  data-profile="Seeker"
                  aria-selected={proofRiskProfile === 'Seeker'}
                  className={proofRiskProfile === 'Seeker' ? 'active' : ''}
                  onClick={() => setProofRiskProfile('Seeker')}
                >
                  {t('landing.hero.riskProfiles.seeker')}
                </button>
              </div>
            </div>
            <div ref={proofChartRef} style={{ width: '100%', height: '500px', margin: '0 auto' }}></div>
            <p className="proof-disclaimer">{t('landing.proof.disclaimer')}</p>
          </div>

          <div className="proof-metrics">
            <div 
              className="metric-card animated"
              style={{
                borderLeftColor: profileThemes[proofRiskProfile].accent,
                transition: 'border-left-color 0.6s cubic-bezier(0.4, 0, 0.2, 1)'
              }}
            >
              <div className="metric-label">{t('landing.proof.metrics.beatsSpy')}</div>
              <div 
                className="metric-value"
                style={{
                  color: profileThemes[proofRiskProfile].accent,
                  transition: 'color 0.6s cubic-bezier(0.4, 0, 0.2, 1)'
                }}
              >
                {proofMetrics.beatsBenchmark}%
              </div>
            </div>
            <div 
              className="metric-card animated"
              style={{
                borderLeftColor: profileThemes[proofRiskProfile].accent,
                transition: 'border-left-color 0.6s cubic-bezier(0.4, 0, 0.2, 1)'
              }}
            >
              <div className="metric-label">{t('landing.proof.metrics.avgReturn')}</div>
              <div 
                className="metric-value"
                style={{
                  color: profileThemes[proofRiskProfile].accent,
                  transition: 'color 0.6s cubic-bezier(0.4, 0, 0.2, 1)'
                }}
              >
                {proofMetrics.avgReturn}%
              </div>
            </div>
            <div 
              className="metric-card animated"
              style={{
                borderLeftColor: profileThemes[proofRiskProfile].accent,
                transition: 'border-left-color 0.6s cubic-bezier(0.4, 0, 0.2, 1)'
              }}
            >
              <div className="metric-label">{t('landing.proof.metrics.volatility')}</div>
              <div 
                className="metric-value"
                style={{
                  color: profileThemes[proofRiskProfile].accent,
                  transition: 'color 0.6s cubic-bezier(0.4, 0, 0.2, 1)'
                }}
              >
                {proofMetrics.volatility}%
              </div>
            </div>
            <div 
              className="metric-card animated"
              style={{
                borderLeftColor: profileThemes[proofRiskProfile].accent,
                transition: 'border-left-color 0.6s cubic-bezier(0.4, 0, 0.2, 1)'
              }}
            >
              <div className="metric-label">{t('landing.proof.metrics.excessReturn')}</div>
              <div 
                className="metric-value" 
                id="metric-risk-return"
                style={{
                  color: profileThemes[proofRiskProfile].accent,
                  transition: 'color 0.6s cubic-bezier(0.4, 0, 0.2, 1)'
                }}
              >
                {getExcessReturnValue(proofRiskProfile)}
              </div>
            </div>
            <div 
              className="metric-card animated"
              style={{
                borderLeftColor: profileThemes[proofRiskProfile].accent,
                transition: 'border-left-color 0.6s cubic-bezier(0.4, 0, 0.2, 1)'
              }}
            >
              <div className="metric-label">{t('landing.proof.metrics.drawdown')}</div>
              <div 
                className="metric-value"
                style={{
                  color: profileThemes[proofRiskProfile].accent,
                  transition: 'color 0.6s cubic-bezier(0.4, 0, 0.2, 1)'
                }}
              >
                {proofMetrics.maxDrawdown}%
              </div>
            </div>
          </div>

          <div className="proof-pillars">
            {t('landing.proof.pillars', { returnObjects: true }).map((pillar, index) => (
              <div key={index} className="pillar-card">
                <h3 className="pillar-title">{pillar.title}</h3>
                <p className="pillar-description">{pillar.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Product Section */}
      <section className="product-section">
        <div className="product-container">
          <h2 className="product-headline">
            <span className="product-headline-top">{t('landing.product.headlineTop')}</span>
            <span className="product-headline-bottom">{t('landing.product.headlineBottom')}</span>
          </h2>
          <p className="product-subheadline">{t('landing.product.subheadline')}</p>
          
          <img src="/images/dashboards/dashboard.png" alt="Altradenative Dashboard" className="product-image" />

          <ul className="product-features">
            {t('landing.product.features', { returnObjects: true }).map((feature, index) => (
              <li key={index} className="product-feature-item">
                <svg className="checkmark-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                  <path className="checkmark-path" d="M20 6L9 17l-5-5"/>
                </svg>
                <span className="feature-text">{feature}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* How It Works Section */}
      <section className="how-it-works-section">
        <div className="how-it-works-container">
          <h2 className="how-it-works-headline">
            <span className="how-it-works-headline-top">{t('landing.howItWorks.headline')}</span>
          </h2>
          <p className="how-it-works-subheadline">{t('landing.howItWorks.subheadline')}</p>
          
          <div className="process-flow">
            <svg className="process-line" viewBox="0 0 800 2" preserveAspectRatio="none">
              <defs>
                <linearGradient id="lineGradient" x1="0%" y1="0%" x2="100%" y2="0%">
                  <stop offset="0%" style={{ stopColor: 'var(--accent)', stopOpacity: 0.3 }} />
                  <stop offset="50%" style={{ stopColor: 'var(--accent)', stopOpacity: 0.6 }} />
                  <stop offset="100%" style={{ stopColor: 'var(--accent)', stopOpacity: 0.3 }} />
                </linearGradient>
                <filter id="glow">
                  <feGaussianBlur stdDeviation="2" result="coloredBlur"/>
                  <feMerge>
                    <feMergeNode in="coloredBlur"/>
                    <feMergeNode in="SourceGraphic"/>
                  </feMerge>
                </filter>
              </defs>
              <path 
                className="process-line-main" 
                d="M 0 1 L 800 1" 
                stroke="url(#lineGradient)" 
                strokeWidth="2" 
                fill="none" 
                strokeDasharray="800" 
                strokeDashoffset="800" 
                filter="url(#glow)"
              />
              <path 
                className="process-line-glow" 
                d="M 0 1 L 800 1" 
                stroke="var(--accent)" 
                strokeWidth="4" 
                fill="none" 
                strokeDasharray="800" 
                strokeDashoffset="800" 
                opacity="0.2"
              />
            </svg>
            
            <div className="process-particles">
              <div className="particle particle-1"></div>
              <div className="particle particle-2"></div>
              <div className="particle particle-3"></div>
            </div>
            
            <div className="process-nodes">
              {t('landing.howItWorks.steps', { returnObjects: true }).map((step, index) => (
                <div key={index} className="process-node" data-node={index + 1}>
                  <div className="process-icon-wrapper">
                    <div className="process-icon" aria-hidden="true">
                      <img 
                        src={index === 0 ? '/images/misc/Group 11911.png' : index === 1 ? '/images/misc/Group 11907.png' : '/images/misc/Group 11908.png'} 
                        alt="" 
                      />
                    </div>
                  </div>
                  <h3 className="process-step-title">{step.title}</h3>
                  <p className="process-step-description">{step.description}</p>
                </div>
              ))}
            </div>
          </div>

          <div className="disclaimer-box">
            <p className="disclaimer-text">
              <strong className="disclaimer-prefix">{t('landing.howItWorks.disclaimer.prefix')}</strong>
              {' '}
              {t('landing.howItWorks.disclaimer.text')}
            </p>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="cta-section">
        <div className="cta-container">
          <h2 className="cta-headline">
            <span className="cta-headline-top">{t('landing.cta.headlineTop')}</span>
            <span className="cta-headline-bottom">{t('landing.cta.headlineBottom')}</span>
          </h2>
          <p className="cta-subheadline">{t('landing.cta.subheadline')}</p>
          
          <div className="cta-buttons">
            <Link to="/subscriptions" className="cta-button-primary">
              {t('landing.cta.buttons.primary')}
            </Link>
            <Link to="/subscriptions" className="cta-button-secondary">
              {t('landing.cta.buttons.secondary')}
            </Link>
          </div>
        </div>
      </section>

      {/* Footer Section */}
      <footer className="footer-section">
        <div className="footer-container">
          <div className="footer-content">
            <div className="footer-branding">
              <img src="/images/logos/logo.svg" alt="Altradenative" className="footer-company-logo" />
              <p className="footer-tagline">{t('landing.footer.tagline')}</p>
              <p className="footer-founding-partners">{t('landing.footer.foundingPartners')}</p>
              <div className="footer-partner-logos">
                <img src="/images/logos/HEDGIT_Logo-BIG.png" alt="Hedgit" className="footer-partner-logo" />
                <img src="/images/icons/Dyhedge.svg" alt="DyHedge" className="footer-partner-logo" />
                <img src="/images/logos/Valcereal Logo.svg" alt="Valcereal" className="footer-partner-logo" />
              </div>
            </div>

            <div className="footer-links">
              <h4 className="footer-links-title">{t('landing.footer.links.legal')}</h4>
              <ul className="footer-links-list">
                <li>
                  <button 
                    className="footer-link" 
                    onClick={() => handleModalOpen('about')}
                    style={{ background: 'none', border: 'none', color: 'inherit', cursor: 'pointer', textAlign: 'left', width: '100%' }}
                  >
                    {t('landing.footer.links.aboutUs')}
                  </button>
                </li>
                <li>
                  <button 
                    className="footer-link" 
                    onClick={() => handleModalOpen('terms')}
                    style={{ background: 'none', border: 'none', color: 'inherit', cursor: 'pointer', textAlign: 'left', width: '100%' }}
                  >
                    {t('landing.footer.links.termsOfService')}
                  </button>
                </li>
                <li>
                  <button 
                    className="footer-link" 
                    onClick={() => handleModalOpen('privacy')}
                    style={{ background: 'none', border: 'none', color: 'inherit', cursor: 'pointer', textAlign: 'left', width: '100%' }}
                  >
                    {t('landing.footer.links.privacyPolicy')}
                  </button>
                </li>
              </ul>
              <h4 className="footer-links-title">{t('landing.footer.links.contact')}</h4>
              <ul className="footer-links-list">
                <li><a href={`mailto:${t('landing.footer.links.email')}`} className="footer-link">{t('landing.footer.links.email')}</a></li>
              </ul>
              <h4 className="footer-links-title">{t('landing.footer.links.help')}</h4>
              <ul className="footer-links-list">
                <li>
                  <button 
                    className="footer-link" 
                    onClick={() => handleModalOpen('faq')}
                    style={{ background: 'none', border: 'none', color: 'inherit', cursor: 'pointer', textAlign: 'left', width: '100%' }}
                  >
                    {t('landing.footer.links.faq')}
                  </button>
                </li>
              </ul>
            </div>

            <div className="footer-legal">
              <p className="footer-copyright">
                {t('landing.footer.copyright', { year: new Date().getFullYear() })}
              </p>
              <p className="footer-company-info">
                {t('landing.footer.companyInfo')}
              </p>
              <p className="footer-disclaimer">
                {t('landing.footer.disclaimer')}
              </p>
            </div>
          </div>
        </div>
      </footer>

      {/* Modals */}
      {/* About Modal */}
      {openModal === 'about' && (
        <div 
          className="modal" 
          role="dialog" 
          aria-labelledby="modal-about-title" 
          aria-hidden="false"
          onClick={(e) => {
            if (e.target.className === 'modal-backdrop' || e.target.className === 'modal') {
              handleModalClose();
            }
          }}
        >
          <div className="modal-backdrop"></div>
          <div className="modal-container">
            <button className="modal-close" onClick={handleModalClose} aria-label={t('landing.modals.close')}>&times;</button>
            <div className="modal-content">
              <h2 id="modal-about-title" className="modal-title">{t('landing.modals.about.title')}</h2>
              <div className="modal-body">
                <p>{t('landing.modals.about.content')}</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Terms Modal */}
      {openModal === 'terms' && (
        <div 
          className="modal" 
          role="dialog" 
          aria-labelledby="modal-terms-title" 
          aria-hidden="false"
          onClick={(e) => {
            if (e.target.className === 'modal-backdrop' || e.target.className === 'modal') {
              handleModalClose();
            }
          }}
        >
          <div className="modal-backdrop"></div>
          <div className="modal-container">
            <button className="modal-close" onClick={handleModalClose} aria-label={t('landing.modals.close')}>&times;</button>
            <div className="modal-content">
              <h2 id="modal-terms-title" className="modal-title">{t('landing.modals.terms.title')}</h2>
              <div className="modal-body">
                <p className="modal-updated">{t('landing.modals.terms.updated')}</p>
                
                {i18n.language === 'es' ? (
                  <>
                    <h3>1. Aceptación de los Términos</h3>
                    <p>Al acceder o usar la plataforma de Altradenative ("Servicio"), aceptas estar sujeto a estos Términos de Servicio ("Términos"). Si no estás de acuerdo con estos Términos, no uses el Servicio.</p>

                    <h3>2. Descripción del Servicio</h3>
                    <p>Altradenative es una plataforma analítica que proporciona perspectivas de inversión y dashboards generados algorítmicamente únicamente con fines informativos. Altradenative no ofrece asesoramiento de inversión, ejecuta operaciones ni gestiona fondos de clientes.</p>

                    <h3>3. Elegibilidad</h3>
                    <p>Debes tener al menos 18 años (o la mayoría de edad legal en tu jurisdicción) para usar el Servicio. Al usar el Servicio, declaras y garantizas que cumples con este requisito de edad.</p>

                    <h3>4. Disponibilidad de Servicios</h3>
                    <p>Altradenative se ofrece exclusivamente como una plataforma educativa y analítica. El servicio no está disponible para residentes o ciudadanos de los Estados Unidos de América. Al acceder o utilizar esta plataforma, usted declara y garantiza que no se encuentra ubicado en, constituido en, ni es residente de los Estados Unidos. Cualquier intento de acceder al servicio desde dentro de los Estados Unidos es no autorizado y estrictamente prohibido.</p>
                    <p>Altradenative se reserva el derecho de bloquear el acceso, cancelar suscripciones o terminar cuentas que se encuentren en violación de esta restricción. Los usuarios son los únicos responsables de asegurar que el uso de la plataforma cumpla con las leyes y regulaciones aplicables en su jurisdicción.</p>

                    <h3>5. Suscripción y Acceso</h3>
                    <ul>
                      <li><strong>Planes de Suscripción:</strong> El acceso a los dashboards y análisis se proporciona mediante planes de suscripción según se describe en nuestro sitio web. Los planes pueden incluir acceso diario, mensual o de prueba.</li>
                      <li><strong>Prueba Gratuita:</strong> Una prueba gratuita única está disponible para usuarios elegibles.</li>
                      <li><strong>Renovaciones y Cancelaciones:</strong> Las suscripciones mensuales se renuevan automáticamente a menos que se cancelen. Puedes cancelar en cualquier momento a través de la configuración de tu cuenta.</li>
                      <li><strong>Pago:</strong> Todas las tarifas se divulgan claramente antes de la compra. Los pagos se procesan de forma segura a través de proveedores de terceros y pueden estar sujetos a impuestos o tarifas locales.</li>
                    </ul>

                    <h3>6. Uso del Servicio</h3>
                    <ul>
                      <li>Aceptas usar el Servicio únicamente para fines personales y no comerciales.</li>
                      <li>Eres responsable de interpretar y utilizar las perspectivas proporcionadas. Altradenative no acepta órdenes de trading ni proporciona asesoramiento de inversión personalizado.</li>
                      <li>Debes usar tu propio bróker para ejecutar cualquier operación basada en información del Servicio.</li>
                    </ul>

                    <h3>7. Propiedad Intelectual</h3>
                    <p>Todo el contenido, algoritmos, dashboards y materiales proporcionados por Altradenative son propiedad intelectual de Hedgit LLC y sus socios. No puedes copiar, reproducir, distribuir o crear obras derivadas sin permiso expreso por escrito, excepto según lo permitido por la ley aplicable en tu jurisdicción.</p>

                    <h3>8. Datos y Privacidad</h3>
                    <p>Tu uso del Servicio está sujeto a nuestra Política de Privacidad, que explica cómo recopilamos, usamos y protegemos tu información personal. Al usar el Servicio, consientes la recopilación y el uso de tus datos según se describe en la Política de Privacidad.</p>
                    <p>Altradenative cumple con las principales regulaciones internacionales de protección de datos, incluyendo el Reglamento General de Protección de Datos (GDPR) para usuarios en el Espacio Económico Europeo (EEE), y otras leyes locales aplicables. Tienes derecho a acceder, corregir o eliminar tus datos personales, y a retirar el consentimiento cuando lo requiera la ley.</p>

                    <h3>9. Avisos Legales</h3>
                    <ul>
                      <li><strong>Sin Asesoramiento de Inversión:</strong> Toda la información proporcionada es únicamente con fines informativos y analíticos y no constituye asesoramiento de trading o inversión.</li>
                      <li><strong>Sin Garantía de Resultados:</strong> El rendimiento pasado, incluyendo datos de backtesting, no garantiza resultados futuros. Invertir en acciones y ETFs conlleva el riesgo de pérdida de capital.</li>
                      <li><strong>Sin Relación Fiduciaria:</strong> Altradenative no es un bróker, asesor ni fiduciario. Eres el único responsable de tus decisiones de inversión.</li>
                    </ul>

                    <h3>10. Limitación de Responsabilidad</h3>
                    <p>En la máxima medida permitida por la ley aplicable, Altradenative, Hedgit LLC, DyHedge, Valcereal y sus afiliados no serán responsables de ningún daño directo, indirecto, incidental, consecuencial o punitivo que surja del uso del Servicio, incluyendo cualquier pérdida de fondos o pérdidas de inversión.</p>
                    <p>Algunas jurisdicciones no permiten la exclusión o limitación de ciertas responsabilidades, por lo que estas limitaciones pueden no aplicarse a ti.</p>

                    <h3>11. Indemnización</h3>
                    <p>Aceptas indemnizar y eximir de responsabilidad a Altradenative, Hedgit LLC, DyHedge, Valcereal y sus afiliados de cualquier reclamo, daño, pérdida o gasto (incluyendo honorarios legales) que surja de tu uso del Servicio o violación de estos Términos, excepto cuando esté prohibido por la ley aplicable.</p>

                    <h3>12. Modificaciones al Servicio y a los Términos</h3>
                    <p>Altradenative se reserva el derecho de modificar o discontinuar el Servicio en cualquier momento, con o sin previo aviso. Podemos actualizar estos Términos de vez en cuando; los cambios se publicarán en esta página con una fecha de vigencia actualizada. El uso continuo del Servicio constituye la aceptación de los Términos revisados.</p>
                    <p>Cuando lo requiera la ley, serás notificado de cambios materiales y se te puede pedir que aceptes los Términos actualizados.</p>

                    <h3>13. Ley Aplicable y Jurisdicción</h3>
                    <p>Estos Términos se rigen por las leyes de Estados Unidos y del Estado de Delaware, excepto cuando las leyes locales requieran lo contrario. Para usuarios que residen en la Unión Europea, Reino Unido u otras jurisdicciones con leyes obligatorias de protección al consumidor, estos Términos se interpretarán de acuerdo con dichas leyes.</p>
                    <p>Cualquier disputa que surja de estos Términos o tu uso del Servicio puede estar sujeta a la jurisdicción exclusiva de los tribunales en tu país de residencia, según lo requiera la ley aplicable.</p>

                    <h3>14. Derechos del Consumidor</h3>
                    <p>Nada en estos Términos limita los derechos del consumidor que puedas tener bajo las leyes de tu país de residencia. Si estás ubicado en la Unión Europea, puedes tener derechos adicionales con respecto a reembolsos, retiro y resolución de disputas.</p>

                    <h3>15. Idioma</h3>
                    <p>Estos Términos pueden proporcionarse en múltiples idiomas para conveniencia. En caso de conflicto o inconsistencia entre las versiones traducidas, la versión en español prevalecerá a menos que la ley requiera lo contrario.</p>

                    <h3>16. Información de Contacto</h3>
                    <p>Para preguntas o inquietudes sobre estos Términos, por favor contáctanos en <a href="mailto:contact@hedgit.ai">contact@hedgit.ai</a>.</p>

                    <h3>Apéndice A: Argentina</h3>
                    <h4>1. Protección al Consumidor</h4>
                    <ul>
                      <li>Altradenative cumple con la Ley de Defensa del Consumidor No. 24.240 de Argentina y las regulaciones relacionadas.</li>
                      <li>Los usuarios argentinos tienen derecho a información clara, precisa y accesible sobre el servicio, precios y términos.</li>
                      <li>Los usuarios pueden retirarse de los contratos de suscripción dentro de cinco (5) días desde la compra, según la ley local, y solicitar reembolsos por servicios no prestados o que no cumplan con los estándares contractuales.</li>
                      <li>La resolución de disputas está disponible a través de las autoridades de protección al consumidor de Argentina.</li>
                    </ul>
                    <h4>2. Privacidad de Datos</h4>
                    <ul>
                      <li>Altradenative cumple con la Ley de Protección de Datos Personales 25.326 de Argentina (PDPA).</li>
                      <li>Los datos personales se procesan solo con consentimiento explícito e informado. Los usuarios tienen derecho a acceder, corregir, actualizar o eliminar sus datos personales en cualquier momento.</li>
                      <li>Las bases de datos que contienen datos personales están registradas en la Agencia de Acceso a la Información Pública.</li>
                      <li>Las transferencias de datos fuera de Argentina se realizan de acuerdo con la PDPA y los estándares internacionales de adecuación.</li>
                      <li>Para consultas relacionadas con la privacidad, los usuarios pueden contactar a info@aaip.gob.ar o visitar la Autoridad de Protección de Datos de Argentina.</li>
                    </ul>
                    <h4>3. Idioma</h4>
                    <p>La versión en español de estos Términos prevalece para usuarios en Argentina. Se proporciona una versión en inglés para conveniencia.</p>
                    <h4>4. Jurisdicción</h4>
                    <p>Cualquier disputa que surja de estos Términos o el uso del Servicio por residentes argentinos puede resolverse ante los tribunales competentes de la Ciudad de Buenos Aires, a menos que la ley requiera lo contrario.</p>

                    <h3>Apéndice B: Estados Unidos</h3>
                    <h4>1. Protección al Consumidor</h4>
                    <ul>
                      <li>Altradenative cumple con todas las leyes federales y estatales de protección al consumidor aplicables, incluyendo la Ley de la Comisión Federal de Comercio y los estatutos estatales relevantes.</li>
                      <li>Los usuarios tienen derecho a divulgaciones claras sobre las características del servicio, precios, términos de suscripción y políticas de cancelación.</li>
                      <li>Los reembolsos y cancelaciones se manejan de acuerdo con la ley estadounidense y los términos específicos descritos en el acuerdo principal.</li>
                      <li>Las cláusulas de arbitraje y renuncia a acciones colectivas pueden aplicarse, según lo permitido por la ley.</li>
                    </ul>
                    <h4>2. Privacidad de Datos</h4>
                    <ul>
                      <li>Altradenative cumple con las leyes federales y estatales de privacidad de datos de EE.UU., incluyendo pero no limitándose a la Ley de Privacidad del Consumidor de California (CCPA), la Ley de Derechos de Privacidad de California (CPRA) y otras regulaciones específicas del estado.</li>
                      <li>Los usuarios tienen derecho a saber qué datos personales se recopilan, solicitar acceso, corrección, eliminación y optar por no participar en la venta o el intercambio de datos, según lo requiera la ley estatal aplicable.</li>
                      <li>Las notificaciones de violación de datos se proporcionarán de acuerdo con los requisitos federales y estatales.</li>
                      <li>Para residentes de California: Puedes ejercer tus derechos de privacidad contactándonos en <a href="mailto:contact@hedgit.ai">contact@hedgit.ai</a> o siguiendo las instrucciones en nuestra Política de Privacidad.</li>
                    </ul>
                    <h4>3. Jurisdicción</h4>
                    <p>Estos Términos se rigen por las leyes de Estados Unidos y del Estado de Delaware, excepto cuando las leyes locales requieran lo contrario.</p>
                    <p>Las disputas pueden estar sujetas a arbitraje vinculante y/o la jurisdicción exclusiva de los tribunales en Delaware, a menos que la ley requiera lo contrario.</p>

                    <h3>Apéndice C: Uso Autorizado y Confidencialidad</h3>
                    <h4>1. Solo Uso Autorizado</h4>
                    <p>La suscripción otorga al usuario un derecho personal, no transferible, no exclusivo para acceder al dashboard de Altradenative. El acceso está estrictamente limitado al individuo o entidad suscrita. Cualquier compartir, sublicenciar o redistribución del contenido del dashboard a no suscriptores está expresamente prohibido.</p>

                    <h4>2. Confidencialidad de las Perspectivas</h4>
                    <p>Todos los datos, análisis y perspectivas mostrados en el dashboard son información confidencial de Altradenative. Los suscriptores aceptan mantener la confidencialidad de dicha información y no divulgar, reproducir o distribuirla a terceros sin el consentimiento previo por escrito de Altradenative.</p>

                    <h4>3. Prohibición de Compartir No Autorizado</h4>
                    <p>Los suscriptores no pueden proporcionar capturas de pantalla, descargas, exportaciones o cualquier otra reproducción del contenido del dashboard a no suscriptores. Cualquier intento de compartir o diseminar datos del dashboard fuera de la cuenta autorizada constituye una violación material de estos Términos.</p>

                    <h4>4. Monitoreo y Cumplimiento</h4>
                    <p>Altradenative se reserva el derecho de monitorear los patrones de uso para detectar compartir no autorizado. En caso de uso indebido sospechado o confirmado, Altradenative puede suspender o rescindir la suscripción sin reembolso y buscar recursos legales.</p>

                    <h4>5. Responsabilidad por Uso Indebido</h4>
                    <p>Los suscriptores son los únicos responsables de cualquier uso indebido de los datos del dashboard originados en su cuenta. El compartir no autorizado puede resultar en daños, penalizaciones u obligaciones de indemnización a Altradenative.</p>

                    <h4>6. Protección de Propiedad Intelectual</h4>
                    <p>Todo el contenido del dashboard, incluyendo análisis, algoritmos y visualizaciones, sigue siendo propiedad intelectual de Hedgit LLC y sus socios. El uso o distribución no autorizado puede constituir una infracción y estará sujeto a aplicación bajo las leyes de propiedad intelectual aplicables.</p>

                    <h4>7. Sin Elusión</h4>
                    <p>Los suscriptores aceptan no intentar eludir las restricciones de suscripción, salvaguardas técnicas o controles de acceso diseñados para proteger los datos del dashboard. Cualquier esfuerzo de elusión será tratado como una violación de estos Términos.</p>
                  </>
                ) : (
                  <>
                    <h3>1. Acceptance of Terms</h3>
                    <p>By accessing or using the Altradenative platform ("Service"), you agree to be bound by these Terms of Service ("Terms"). If you do not agree to these Terms, do not use the Service.</p>

                    <h3>2. Service Overview</h3>
                    <p>Altradenative is an analytics platform providing algorithmically generated investment insights and dashboards for informational purposes only. Altradenative does not offer trading advice, execute trades, or manage client funds.</p>

                    <h3>3. Eligibility</h3>
                    <p>You must be at least 18 years old (or the legal age of majority in your jurisdiction) to use the Service. By using the Service, you represent and warrant that you meet this age requirement.</p>

                    <h3>4. Availability of Services</h3>
                    <p>Altradenative is offered exclusively as an educational and analytical platform. The service is not available to residents or citizens of the United States of America. By accessing or using this platform, you represent and warrant that you are not located in, incorporated in, or a resident of the United States. Any attempt to access the service from within the United States is unauthorized and strictly prohibited.</p>
                    <p>Altradenative reserves the right to block access, cancel subscriptions, or terminate accounts that are found to be in violation of this restriction. Users are solely responsible for ensuring that their use of the platform complies with applicable laws and regulations in their jurisdiction.</p>

                    <h3>5. Subscription and Access</h3>
                    <ul>
                      <li><strong>Subscription Plans:</strong> Access to dashboards and analytics is provided via subscription plans as described on our website. Plans may include daily, monthly, or trial access.</li>
                      <li><strong>Free Trial:</strong> A one-time free trial is available for eligible users.</li>
                      <li><strong>Renewals and Cancellations:</strong> Monthly subscriptions renew automatically unless canceled. You may cancel at any time via your account settings.</li>
                      <li><strong>Payment:</strong> All fees are clearly disclosed prior to purchase. Payments are processed securely via third-party providers and may be subject to local taxes or fees.</li>
                    </ul>

                    <h3>6. Use of Service</h3>
                    <ul>
                      <li>You agree to use the Service solely for personal, non-commercial purposes.</li>
                      <li>You are responsible for interpreting and utilizing the insights provided. Altradenative does not accept trading orders or provide personalized investment advice.</li>
                      <li>You must use your own broker to execute any trades based on information from the Service.</li>
                    </ul>

                    <h3>7. Intellectual Property</h3>
                    <p>All content, algorithms, dashboards, and materials provided by Altradenative are the intellectual property of Hedgit LLC and its partners. You may not copy, reproduce, distribute, or create derivative works without express written permission, except as permitted by applicable law in your jurisdiction.</p>

                    <h3>8. Data and Privacy</h3>
                    <p>Your use of the Service is subject to our Privacy Policy, which explains how we collect, use, and protect your personal information. By using the Service, you consent to the collection and use of your data as described in the Privacy Policy.</p>
                    <p>Altradenative complies with major international data protection regulations, including the General Data Protection Regulation (GDPR) for users in the European Economic Area (EEA), and other applicable local laws. You have the right to access, correct, or delete your personal data, and to withdraw consent where required by law.</p>

                    <h3>9. Disclaimers</h3>
                    <ul>
                      <li><strong>No Investment Advice:</strong> All information provided is for informational and analytical purposes only and does not constitute trading or investment advice.</li>
                      <li><strong>No Guarantee of Results:</strong> Past performance, including backtested data, does not guarantee future results. Investing in stocks and ETFs carries the risk of capital loss.</li>
                      <li><strong>No Fiduciary Relationship:</strong> Altradenative is not a broker, advisor, or fiduciary. You are solely responsible for your investment decisions.</li>
                    </ul>

                    <h3>10. Limitation of Liability</h3>
                    <p>To the maximum extent permitted by applicable law, Altradenative, Hedgit LLC, DyHedge, Valcereal, and their affiliates shall not be liable for any direct, indirect, incidental, consequential, or punitive damages arising out of your use of the Service, including any loss of funds or investment losses.</p>
                    <p>Some jurisdictions do not allow the exclusion or limitation of certain liabilities, so these limitations may not apply to you.</p>

                    <h3>11. Indemnification</h3>
                    <p>You agree to indemnify and hold harmless Altradenative, Hedgit LLC, DyHedge, Valcereal, and their affiliates from any claims, damages, losses, or expenses (including legal fees) arising from your use of the Service or violation of these Terms, except where prohibited by applicable law.</p>

                    <h3>12. Modifications to Service and Terms</h3>
                    <p>Altradenative reserves the right to modify or discontinue the Service at any time, with or without notice. We may update these Terms from time to time; changes will be posted on this page with an updated effective date. Continued use of the Service constitutes acceptance of the revised Terms.</p>
                    <p>Where required by law, you will be notified of material changes and may be asked to accept the updated Terms.</p>

                    <h3>13. Governing Law and Jurisdiction</h3>
                    <p>These Terms are governed by the laws of the United States and the State of Delaware, except where local laws require otherwise. For users residing in the European Union, United Kingdom, or other jurisdictions with mandatory consumer protection laws, these Terms shall be interpreted in accordance with such laws.</p>
                    <p>Any disputes arising from these Terms or your use of the Service may be subject to the exclusive jurisdiction of the courts in your country of residence, as required by applicable law.</p>

                    <h3>14. Consumer Rights</h3>
                    <p>Nothing in these Terms limits any consumer rights you may have under the laws of your country of residence. If you are located in the European Union, you may have additional rights regarding refunds, withdrawal, and dispute resolution.</p>

                    <h3>15. Language</h3>
                    <p>These Terms may be provided in multiple languages for convenience. In case of any conflict or inconsistency between translated versions, the English version shall prevail unless otherwise required by law.</p>

                    <h3>16. Contact Information</h3>
                    <p>For questions or concerns regarding these Terms, please contact us at <a href="mailto:contact@hedgit.ai">contact@hedgit.ai</a>.</p>

                    <h3>Appendix A: Argentina</h3>
                    <h4>1. Consumer Protection</h4>
                    <ul>
                      <li>Altradenative complies with Argentina's Consumer Protection Law No. 24.240 (Ley de Defensa del Consumidor) and related regulations.</li>
                      <li>Argentine users have the right to clear, accurate, and accessible information about the service, pricing, and terms.</li>
                      <li>Users may withdraw from subscription contracts within five (5) days of purchase, as per local law, and request refunds for services not rendered or that do not meet contractual standards.</li>
                      <li>Dispute resolution is available through Argentina's consumer protection authorities.</li>
                    </ul>
                    <h4>2. Data Privacy</h4>
                    <ul>
                      <li>Altradenative complies with Argentina's Personal Data Protection Act 25.326 (Ley de Protección de los Datos Personales, PDPA).</li>
                      <li>Personal data is processed only with explicit, informed consent. Users have the right to access, correct, update, or delete their personal data at any time.</li>
                      <li>Databases containing personal data are registered with the Agency for Access to Public Information (Agencia de Acceso a la Información Pública).</li>
                      <li>Data transfers outside Argentina are conducted in accordance with PDPA and international adequacy standards.</li>
                      <li>For privacy-related inquiries, users may contact info@aaip.gob.ar or visit Argentina's Data Protection Authority.</li>
                    </ul>
                    <h4>3. Language</h4>
                    <p>The Spanish version of these Terms prevails for users in Argentina. An English version is provided for convenience.</p>
                    <h4>4. Jurisdiction</h4>
                    <p>Any disputes arising from these Terms or the use of the Service by Argentine residents may be resolved before the competent courts of the City of Buenos Aires, unless otherwise required by law.</p>

                    <h3>Appendix B: United States</h3>
                    <h4>1. Consumer Protection</h4>
                    <ul>
                      <li>Altradenative complies with all applicable federal and state consumer protection laws, including the Federal Trade Commission Act and relevant state statutes.</li>
                      <li>Users are entitled to clear disclosures regarding service features, pricing, subscription terms, and cancellation policies.</li>
                      <li>Refunds and cancellations are handled in accordance with U.S. law and the specific terms outlined in the main agreement.</li>
                      <li>Arbitration and class action waiver clauses may apply, as permitted by law.</li>
                    </ul>
                    <h4>2. Data Privacy</h4>
                    <ul>
                      <li>Altradenative complies with U.S. federal and state data privacy laws, including but not limited to the California Consumer Privacy Act (CCPA), California Privacy Rights Act (CPRA), and other state-specific regulations.</li>
                      <li>Users have the right to know what personal data is collected, request access, correction, deletion, and opt out of data sales or sharing, as required by applicable state law.</li>
                      <li>Data breach notifications will be provided in accordance with federal and state requirements.</li>
                      <li>For California residents: You may exercise your privacy rights by contacting us at <a href="mailto:contact@hedgit.ai">contact@hedgit.ai</a> or following instructions in our Privacy Policy.</li>
                    </ul>
                    <h4>3. Jurisdiction</h4>
                    <p>These Terms are governed by the laws of the United States and the State of Delaware, except where local laws require otherwise.</p>
                    <p>Disputes may be subject to binding arbitration and/or the exclusive jurisdiction of courts in Delaware, unless otherwise required by law.</p>

                    <h3>Appendix C: Authorized Use and Confidentiality</h3>
                    <h4>1. Authorized Use Only</h4>
                    <p>The subscription grants the user a personal, non‑transferable, non‑exclusive right to access the Altradenative dashboard. Access is strictly limited to the subscribed individual or entity. Any sharing, sublicensing, or redistribution of dashboard content to non‑subscribers is expressly prohibited.</p>

                    <h4>2. Confidentiality of Insights</h4>
                    <p>All data, analytics, and insights displayed in the dashboard are confidential information of Altradenative. Subscribers agree to maintain the confidentiality of such information and not disclose, reproduce, or distribute it to any third party without prior written consent from Altradenative.</p>

                    <h4>3. Prohibition on Unauthorized Sharing</h4>
                    <p>Subscribers may not provide screenshots, downloads, exports, or any other reproduction of dashboard content to non‑subscribers. Any attempt to share or disseminate dashboard data outside the authorized account constitutes a material breach of these Terms.</p>

                    <h4>4. Monitoring and Enforcement</h4>
                    <p>Altradenative reserves the right to monitor usage patterns to detect unauthorized sharing. In the event of suspected or confirmed misuse, Altradenative may suspend or terminate the subscription without refund and pursue legal remedies.</p>

                    <h4>5. Liability for Misuse</h4>
                    <p>Subscribers are solely responsible for any misuse of dashboard data originating from their account. Unauthorized sharing may result in damages, penalties, or indemnification obligations to Altradenative.</p>

                    <h4>6. Intellectual Property Protection</h4>
                    <p>All dashboard content, including analytics, algorithms, and visualizations, remains the intellectual property of Hedgit LLC and its partners. Unauthorized use or distribution may constitute infringement and will be subject to enforcement under applicable intellectual property laws.</p>

                    <h4>7. No Circumvention</h4>
                    <p>Subscribers agree not to attempt to bypass subscription restrictions, technical safeguards, or access controls designed to protect dashboard data. Any circumvention efforts will be treated as a violation of these Terms.</p>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Privacy Modal */}
      {openModal === 'privacy' && (
        <div 
          className="modal" 
          role="dialog" 
          aria-labelledby="modal-privacy-title" 
          aria-hidden="false"
          onClick={(e) => {
            if (e.target.className === 'modal-backdrop' || e.target.className === 'modal') {
              handleModalClose();
            }
          }}
        >
          <div className="modal-backdrop"></div>
          <div className="modal-container">
            <button className="modal-close" onClick={handleModalClose} aria-label={t('landing.modals.close')}>&times;</button>
            <div className="modal-content">
              <h2 id="modal-privacy-title" className="modal-title">{t('landing.modals.privacy.title')}</h2>
              <div className="modal-body">
                <p className="modal-updated">{t('landing.modals.privacy.updated')}</p>
                
                {i18n.language === 'es' ? (
                  <>
                    <h3>1. Introducción</h3>
                    <p>Altradenative ("nosotros", "nos", "nuestro") se compromete a proteger tu privacidad y garantizar la seguridad de tu información personal. Esta Política de Privacidad explica cómo recopilamos, usamos, divulgamos y protegemos tu información cuando usas nuestra plataforma y servicios.</p>

                    <h3>2. Información que Recopilamos</h3>
                    <p>Podemos recopilar los siguientes tipos de información:</p>
                    <ul>
                      <li><strong>Información Personal:</strong> Nombre, dirección de correo electrónico y otros datos de contacto que proporciones al suscribirte o contactarnos.</li>
                      <li><strong>Datos de Uso:</strong> Información sobre cómo interactúas con nuestros dashboards y sitio web, incluyendo datos analíticos como vistas de página, duración de sesión e información del dispositivo.</li>
                      <li><strong>Cookies y Tecnologías de Seguimiento:</strong> Utilizamos cookies y tecnologías similares para mejorar tu experiencia y analizar patrones de uso.</li>
                    </ul>

                    <h3>3. Cómo Usamos tu Información</h3>
                    <p>Tu información se utiliza para:</p>
                    <ul>
                      <li>Proporcionar acceso a nuestros dashboards analíticos y servicios de suscripción.</li>
                      <li>Mejorar y optimizar nuestra plataforma y experiencia del usuario.</li>
                      <li>Comunicarnos contigo sobre actualizaciones, información del servicio y soporte.</li>
                      <li>Analizar datos agregados para métricas de rendimiento y mejoras del servicio.</li>
                    </ul>

                    <h3>4. Compartición y Divulgación de Datos</h3>
                    <ul>
                      <li><strong>Sin Asesoramiento de Trading u Órdenes:</strong> Altradenative no ofrece asesoramiento de trading ni acepta órdenes de trading. Todas las perspectivas son únicamente con fines informativos.</li>
                      <li><strong>Servicios de Terceros:</strong> Podemos usar herramientas de análisis de terceros (como Google Analytics) para comprender los patrones de uso. Estos proveedores pueden procesar datos en nuestro nombre, pero no tienen permitido usarlos para sus propios fines.</li>
                      <li><strong>Cumplimiento Legal:</strong> Podemos divulgar información si lo requiere la ley o para proteger nuestros derechos y los de los usuarios.</li>
                    </ul>

                    <h3>5. Seguridad de los Datos</h3>
                    <p>Implementamos medidas de seguridad estándar de la industria para proteger tu información contra acceso no autorizado, alteración, divulgación o destrucción. Sin embargo, ningún método de transmisión por Internet o almacenamiento electrónico es 100% seguro.</p>

                    <h3>6. Transferencias Internacionales de Datos</h3>
                    <p>Como plataforma global, tu información puede ser procesada y almacenada en países fuera del tuyo. Cumplimos con las leyes de protección de datos aplicables y nos aseguramos de que existan salvaguardas apropiadas.</p>

                    <h3>7. Tus Derechos</h3>
                    <p>Dependiendo de tu jurisdicción, puedes tener derecho a:</p>
                    <ul>
                      <li>Acceder, corregir o eliminar tu información personal.</li>
                      <li>Oponerte o restringir ciertas actividades de procesamiento.</li>
                      <li>Retirar el consentimiento para el procesamiento de datos (cuando sea aplicable).</li>
                    </ul>
                    <p>Para ejercer estos derechos, por favor contáctanos en <a href="mailto:contact@hedgit.ai">contact@hedgit.ai</a>.</p>

                    <h3>8. Privacidad de los Niños</h3>
                    <p>Nuestros servicios no están destinados a personas menores de 18 años. No recopilamos conscientemente información personal de niños.</p>

                    <h3>9. Cambios en Esta Política</h3>
                    <p>Podemos actualizar esta Política de Privacidad de vez en cuando. Los cambios se publicarán en esta página con una fecha de vigencia actualizada.</p>

                    <h3>10. Contáctanos</h3>
                    <p>Si tienes alguna pregunta o inquietud sobre esta Política de Privacidad o nuestras prácticas de datos, por favor contáctanos en <a href="mailto:contact@hedgit.ai">contact@hedgit.ai</a>.</p>
                  </>
                ) : (
                  <>
                    <h3>1. Introduction</h3>
                    <p>Altradenative ("we", "us", "our") is committed to protecting your privacy and ensuring the security of your personal information. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our platform and services.</p>

                    <h3>2. Information We Collect</h3>
                    <p>We may collect the following types of information:</p>
                    <ul>
                      <li><strong>Personal Information:</strong> Name, email address, and other contact details you provide when subscribing or contacting us.</li>
                      <li><strong>Usage Data:</strong> Information about how you interact with our dashboards and website, including analytics data such as page views, session duration, and device information.</li>
                      <li><strong>Cookies and Tracking Technologies:</strong> We use cookies and similar technologies to enhance your experience and analyze usage patterns.</li>
                    </ul>

                    <h3>3. How We Use Your Information</h3>
                    <p>Your information is used to:</p>
                    <ul>
                      <li>Provide access to our analytics dashboards and subscription services.</li>
                      <li>Improve and optimize our platform and user experience.</li>
                      <li>Communicate with you regarding updates, service information, and support.</li>
                      <li>Analyze aggregated data for performance metrics and service improvements.</li>
                    </ul>

                    <h3>4. Data Sharing and Disclosure</h3>
                    <ul>
                      <li><strong>No Trading Advice or Orders:</strong> Altradenative does not offer trading advice or accept trading orders. All insights are for informational purposes only.</li>
                      <li><strong>Third-Party Services:</strong> We may use third-party analytics tools (such as Google Analytics) to understand usage patterns. These providers may process data on our behalf, but are not permitted to use it for their own purposes.</li>
                      <li><strong>Legal Compliance:</strong> We may disclose information if required by law or to protect our rights and users.</li>
                    </ul>

                    <h3>5. Data Security</h3>
                    <p>We implement industry-standard security measures to protect your information from unauthorized access, alteration, disclosure, or destruction. However, no method of transmission over the Internet or electronic storage is 100% secure.</p>

                    <h3>6. International Data Transfers</h3>
                    <p>As a global platform, your information may be processed and stored in countries outside your own. We comply with applicable data protection laws and ensure appropriate safeguards are in place.</p>

                    <h3>7. Your Rights</h3>
                    <p>Depending on your jurisdiction, you may have rights to:</p>
                    <ul>
                      <li>Access, correct, or delete your personal information.</li>
                      <li>Object to or restrict certain processing activities.</li>
                      <li>Withdraw consent for data processing (where applicable).</li>
                    </ul>
                    <p>To exercise these rights, please contact us at <a href="mailto:contact@hedgit.ai">contact@hedgit.ai</a>.</p>

                    <h3>8. Children's Privacy</h3>
                    <p>Our services are not intended for individuals under the age of 18. We do not knowingly collect personal information from children.</p>

                    <h3>9. Changes to This Policy</h3>
                    <p>We may update this Privacy Policy from time to time. Changes will be posted on this page with an updated effective date.</p>

                    <h3>10. Contact Us</h3>
                    <p>If you have any questions or concerns about this Privacy Policy or our data practices, please contact us at <a href="mailto:contact@hedgit.ai">contact@hedgit.ai</a>.</p>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* FAQ Modal */}
      {openModal === 'faq' && (
        <div 
          className="modal" 
          role="dialog" 
          aria-labelledby="modal-faq-title" 
          aria-hidden="false"
          onClick={(e) => {
            if (e.target.className === 'modal-backdrop' || e.target.className === 'modal') {
              handleModalClose();
            }
          }}
        >
          <div className="modal-backdrop"></div>
          <div className="faq-modal-container">
            <button className="modal-close" onClick={handleModalClose} aria-label={t('landing.modals.close')}>&times;</button>
            <div className="faq-header">
              <div className="faq-avatar">💡</div>
              <div className="faq-header-content">
                <h2 id="modal-faq-title" className="faq-title">{t('landing.modals.faq.title')}</h2>
                <p className="faq-subtitle">{t('landing.modals.faq.subtitle')}</p>
              </div>
            </div>
            <div className="faq-search-container">
              <input 
                type="text" 
                className="faq-search" 
                placeholder={t('landing.modals.faq.searchPlaceholder')}
                value={faqSearch}
                onChange={(e) => setFaqSearch(e.target.value)}
              />
            </div>
            <div className="faq-content">
              <div className="faq-categories">
                {Object.entries(t('landing.modals.faq.categories', { returnObjects: true })).map(([key, label]) => (
                  <button
                    key={key}
                    className={`faq-category-btn ${faqCategory === key ? 'active' : ''}`}
                    data-category={key}
                    onClick={() => setFaqCategory(key)}
                  >
                    {label}
                  </button>
                ))}
              </div>
              <div className="faq-items">
                {(() => {
                  // Filter FAQs by category and search
                  let filteredFAQs = faqData;
                  
                  if (faqCategory !== 'all') {
                    filteredFAQs = filteredFAQs.filter(item => item.category === faqCategory);
                  }
                  
                  if (faqSearch.trim()) {
                    const searchLower = faqSearch.toLowerCase();
                    filteredFAQs = filteredFAQs.filter(item => 
                      item.question.toLowerCase().includes(searchLower) ||
                      item.answer.toLowerCase().includes(searchLower)
                    );
                  }
                  
                  // Group by category for display
                  const groupedFAQs = {};
                  filteredFAQs.forEach(item => {
                    if (!groupedFAQs[item.category]) {
                      groupedFAQs[item.category] = [];
                    }
                    groupedFAQs[item.category].push(item);
                  });
                  
                  const categoryNames = {
                    'getting-started': t('landing.modals.faq.categories.gettingStarted'),
                    'subscription': t('landing.modals.faq.categories.subscription'),
                    'platform': t('landing.modals.faq.categories.platform'),
                    'security': t('landing.modals.faq.categories.security'),
                    'investment': t('landing.modals.faq.categories.investment'),
                    'legal': t('landing.modals.faq.categories.legal'),
                    'troubleshooting': t('landing.modals.faq.categories.troubleshooting'),
                    'contact': t('landing.modals.faq.categories.contact')
                  };
                  
                  if (filteredFAQs.length === 0) {
                    return (
                      <div className="faq-no-results">
                        {t('landing.modals.faq.noResults')}
                      </div>
                    );
                  }
                  
                  return Object.keys(groupedFAQs).map(category => (
                    <div key={category} className="faq-section">
                      <h3 className="faq-section-title">{categoryNames[category] || category}</h3>
                      {groupedFAQs[category].map(item => {
                        const isExpanded = expandedFaq === item.id;
                        return (
                          <div key={item.id} className={`faq-item ${isExpanded ? 'expanded' : ''}`}>
                            <div 
                              className="faq-question"
                              onClick={() => {
                                setExpandedFaq(isExpanded ? null : item.id);
                              }}
                            >
                              <span className="faq-question-text">{item.question}</span>
                              <svg className="faq-toggle-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="M6 9l6 6 6-6"/>
                              </svg>
                            </div>
                            <div className="faq-answer">
                              <div 
                                className="faq-answer-content"
                                dangerouslySetInnerHTML={{ __html: item.answer }}
                                onClick={(e) => {
                                  // Handle modal link clicks in FAQ answers
                                  const link = e.target.closest('.faq-modal-link');
                                  if (link) {
                                    e.preventDefault();
                                    const modalType = link.getAttribute('data-modal');
                                    if (modalType) {
                                      setExpandedFaq(null);
                                      setTimeout(() => {
                                        setOpenModal(modalType);
                                      }, 100);
                                    }
                                  }
                                }}
                              />
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  ));
                })()}
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Landing;

